import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Modal, Badge, Spinner, Alert, ListGroup } from 'react-bootstrap';
import { 
  FaMoneyBillWave, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaCheck, 
  FaCheckCircle,
  FaUndo,
  FaReply,
  FaFilter,
  FaFilePdf,
  FaFileExcel,
  FaCog,
  FaImage
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import depositosApi from '../../services/api/depositosApi';
import permisosApi from '../../services/api/permisosApi';
import bancosApi from '../../services/api/bancosApi';
import metodosPagoApi from '../../services/api/metodosPagoApi';
import 'react-toastify/dist/ReactToastify.css';

const defaultFechaSolicitud = () => new Date().toISOString().split('T')[0];

/** Fecha/hora de creación en BD (auditoría) para mostrar en listados */
const formatAuditoriaCreacion = (val) => {
  if (val == null || val === '') return '—';
  try {
    const s = typeof val === 'string' ? val.replace(' ', 'T') : val;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toLocaleString('es-PE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const toInputDate = (val) => {
  if (val == null || val === '') return defaultFechaSolicitud();
  const s = String(val);
  return s.length >= 10 ? s.slice(0, 10) : defaultFechaSolicitud();
};

/** Texto mostrado al elegir un beneficiario en el autocompletado (solo nombre). */
const etiquetaBeneficiarioLista = (b) => {
  if (!b) return '';
  return (b.nombre || '').trim();
};

const Depositos = () => {
  const [depositos, setDepositos] = useState([]);
  const [tiposDeposito, setTiposDeposito] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [beneficiarios, setBeneficiarios] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);
  const [loadingMetodosPago, setLoadingMetodosPago] = useState(false);
  const [loading, setLoading] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showModalBeneficiario, setShowModalBeneficiario] = useState(false);
  const [showModalCuenta, setShowModalCuenta] = useState(false);
  const [showModalConfirmarDeposito, setShowModalConfirmarDeposito] = useState(false);
  const [depositoParaConfirmar, setDepositoParaConfirmar] = useState(null);
  const [showModalDesconfirmar, setShowModalDesconfirmar] = useState(false);
  const [depositoParaDesconfirmar, setDepositoParaDesconfirmar] = useState(null);
  const [showModalDesaprobar, setShowModalDesaprobar] = useState(false);
  const [depositoParaDesaprobar, setDepositoParaDesaprobar] = useState(null);
  const [fechaPagoConfirmacion, setFechaPagoConfirmacion] = useState(new Date().toISOString().split('T')[0]);
  const [numeroOperacionConfirmacion, setNumeroOperacionConfirmacion] = useState('');
  const [metodoPagoConfirmacion, setMetodoPagoConfirmacion] = useState('');
  const [comprobanteConfirmacionFile, setComprobanteConfirmacionFile] = useState(null);
  const [comprobanteConfirmacionPreview, setComprobanteConfirmacionPreview] = useState(null);
  const comprobantePreviewUrlRef = useRef(null);
  const [showModalVerComprobante, setShowModalVerComprobante] = useState(false);
  const [verComprobanteObjectUrl, setVerComprobanteObjectUrl] = useState(null);
  const [loadingVerComprobante, setLoadingVerComprobante] = useState(false);
  const [retencionParams, setRetencionParams] = useState({
    porcentaje: 3,
    monto_minimo_factura: 700
  });
  const [showModalRetencionParams, setShowModalRetencionParams] = useState(false);
  const [formRetencionParams, setFormRetencionParams] = useState({
    porcentaje: '3',
    monto_minimo_factura: '700'
  });
  const [editingDeposito, setEditingDeposito] = useState(null);
  /** con_beneficiario | libre | sin_destinatario (sin FK beneficiario/cuenta; uso temporal según concepto) */
  const [modoDeposito, setModoDeposito] = useState('con_beneficiario');
  const [isMobile, setIsMobile] = useState(false);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [formData, setFormData] = useState({
    centro_costo_id: '',
    tipo_deposito_id: '',
    beneficiario_id: '',
    cuenta_bancaria_id: '',
    numero_documento: '',
    monto: '',
    moneda: 'PEN',
    descripcion: '',
    // Campos para modo libre
    beneficiario: '',
    banco: '',
    cuenta_bancaria: '',
    tipo_cuenta_bancaria_id: '',
    codigo_cuenta: '',
    numero_operacion: '',
    observaciones: '',
    fecha_solicitud: defaultFechaSolicitud()
  });
  
  const [formDataBeneficiario, setFormDataBeneficiario] = useState({
    tipo: 'proveedor',
    nombre: '',
    numero_documento: '',
    tipo_documento_id: '',
    contacto_nombre: '',
    contacto_email: '',
    contacto_telefono: '',
    direccion: '',
    excluido_retencion: false,
    cuentas_bancarias: []
  });
  
  const [mostrarDatosContacto, setMostrarDatosContacto] = useState(false);
  
  const [tiposCuentaBancaria, setTiposCuentaBancaria] = useState([]);
  const [cuentaTemporal, setCuentaTemporal] = useState({
    banco: '',
    numero_cuenta: '',
    tipo_cuenta_bancaria_id: '',
    codigo_cuenta: '',
    moneda: '',
    es_principal: false
  });
  
  const [formDataCuenta, setFormDataCuenta] = useState({
    beneficiario_id: '',
    banco: '',
    numero_cuenta: '',
    tipo_cuenta: '',
    codigo_cuenta: '',
    moneda: 'PEN',
    es_principal: false
  });

  // Filtros
  const [filters, setFilters] = useState(() => {
    const hoy = defaultFechaSolicitud();
    return {
      centro_costo_id: '',
      tipo_deposito_id: '',
      estado: '',
      fecha_desde: hoy,
      fecha_hasta: hoy,
    };
  });

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;

  const [beneficiarioBusqueda, setBeneficiarioBusqueda] = useState('');
  const [beneficiarioSugerenciasAbiertas, setBeneficiarioSugerenciasAbiertas] = useState(false);
  const beneficiarioBlurTimerRef = useRef(null);

  const beneficiariosFiltrados = useMemo(() => {
    const q = beneficiarioBusqueda.trim().toLowerCase();
    if (!q) return [];
    return beneficiarios
      .filter((b) => {
        const nombre = (b.nombre || '').toLowerCase();
        const cod = (b.codigo || '').toLowerCase();
        const doc = (b.numero_documento || '').toLowerCase();
        const tipo = (b.tipo || '').toLowerCase();
        return nombre.includes(q) || cod.includes(q) || doc.includes(q) || tipo.includes(q);
      })
      .slice(0, 25);
  }, [beneficiarios, beneficiarioBusqueda]);

  const seleccionarBeneficiario = (b) => {
    if (beneficiarioBlurTimerRef.current) {
      clearTimeout(beneficiarioBlurTimerRef.current);
      beneficiarioBlurTimerRef.current = null;
    }
    setBeneficiarioBusqueda(etiquetaBeneficiarioLista(b));
    setBeneficiarioSugerenciasAbiertas(false);
    setFormData((prev) => ({
      ...prev,
      beneficiario_id: String(b.id),
      cuenta_bancaria_id: '',
    }));
  };

  const handleBeneficiarioBusquedaChange = (e) => {
    const v = e.target.value;
    setBeneficiarioBusqueda(v);
    setBeneficiarioSugerenciasAbiertas(true);
    const sel = formData.beneficiario_id;
    if (!sel) return;
    const b = beneficiarios.find((x) => String(x.id) === String(sel));
    if (!b) {
      setFormData((prev) => ({ ...prev, beneficiario_id: '', cuenta_bancaria_id: '' }));
      return;
    }
    if (etiquetaBeneficiarioLista(b).toLowerCase() !== v.trim().toLowerCase()) {
      setFormData((prev) => ({ ...prev, beneficiario_id: '', cuenta_bancaria_id: '' }));
    }
  };

  useEffect(() => {
    return () => {
      if (beneficiarioBlurTimerRef.current) clearTimeout(beneficiarioBlurTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (modoDeposito === 'libre') {
      setBeneficiarioBusqueda('');
      setFormData((prev) => ({
        ...prev,
        beneficiario_id: '',
        cuenta_bancaria_id: '',
      }));
      setCuentasBancarias([]);
    } else if (modoDeposito === 'sin_destinatario') {
      setBeneficiarioBusqueda('');
      setFormData((prev) => ({
        ...prev,
        beneficiario_id: '',
        cuenta_bancaria_id: '',
        beneficiario: '',
        banco: '',
        cuenta_bancaria: '',
        tipo_cuenta_bancaria_id: '',
        codigo_cuenta: '',
      }));
      setCuentasBancarias([]);
    }
  }, [modoDeposito]);

  const loadRetencionParametros = useCallback(async () => {
    try {
      const { data } = await depositosApi.getRetencionProveedorParametros();
      setRetencionParams({
        porcentaje: data.porcentaje,
        monto_minimo_factura: data.monto_minimo_factura
      });
    } catch (err) {
      console.warn('No se cargaron parámetros de retención', err);
    }
  }, []);

  useEffect(() => {
    loadTiposDeposito();
    loadCentrosCosto();
    loadBeneficiarios();
    loadBancos();
    loadMetodosPago();
    loadTiposCuentaBancaria();
    checkPermissions();
    
    // Detectar dispositivo móvil
    const checkIfMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    loadRetencionParametros();
  }, [loadRetencionParametros]);

  // Asegurar que los métodos de pago se carguen cuando se abre el modal
  useEffect(() => {
    if (showModalConfirmarDeposito) {
      console.log('🔄 [Depositos] Modal confirmar abierto');
      console.log('📊 [Depositos] metodosPago.length:', metodosPago?.length);
      
      if (!metodosPago || metodosPago.length === 0) {
        console.log('🔄 [Depositos] Cargando métodos de pago...');
        loadMetodosPago();
      }
    }
  }, [showModalConfirmarDeposito, metodosPago]); // ✅ Agregar metodosPago a dependencias

  const checkPermissions = async () => {
    try {
      const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || 'null');
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      
      if (!user?.id) {
        setCanApprove(false);
        return;
      }
      
      // Para vista corporativa, usar proyecto_id = -1
      // Esto permite aprobar depósitos de cualquier proyecto si el usuario tiene el permiso
      let proyectoId = -1; // Vista corporativa por defecto
      if (selectedProject && selectedProject.id && selectedProject.id !== -1) {
        proyectoId = selectedProject.id;
      }
      
      console.log('🔍 [checkPermissions] Verificando permiso depositos.aprobar:', {
        proyectoId,
        selectedProject,
        userId: user.id
      });
      
      const hasPermission = await permisosApi.checkPermission('depositos.aprobar', proyectoId);
      console.log('✅ [checkPermissions] Resultado:', hasPermission);
      
      setCanApprove(hasPermission);
    } catch (error) {
      console.error('❌ [checkPermissions] Error al verificar permisos:', error);
      setCanApprove(false);
    }
  };

  const aplicaVistaRetencionProveedor = () => {
    if (modoDeposito !== 'con_beneficiario' || !formData.beneficiario_id) return false;
    const ben = beneficiarios.find((b) => String(b.id) === String(formData.beneficiario_id));
    const mon = (formData.moneda || 'PEN').toUpperCase();
    return !!(ben && ben.tipo === 'proveedor' && ['PEN', 'USD', 'EUR'].includes(mon) && !ben.excluido_retencion);
  };

  const simboloMonedaRetencion = (cod) => {
    const c = (cod || 'PEN').toUpperCase();
    if (c === 'USD') return 'US$';
    if (c === 'EUR') return '€';
    return 'S/';
  };

  const getRetencionPreview = () => {
    const g = parseFloat(formData.monto);
    if (!Number.isFinite(g) || g <= 0) {
      return { retencion: 0, neto: 0, muestra: false };
    }
    if (!aplicaVistaRetencionProveedor()) {
      return { retencion: 0, neto: g, muestra: false };
    }
    const pct = Number(retencionParams.porcentaje) || 3;
    const minM = Number(retencionParams.monto_minimo_factura) || 700;
    if (g <= minM) {
      return { retencion: 0, neto: g, muestra: true, sinRetPorMinimo: true };
    }
    const ret = Math.round(g * (pct / 100) * 100) / 100;
    const net = Math.round((g - ret) * 100) / 100;
    return { retencion: ret, neto: net, muestra: true, sinRetPorMinimo: false };
  };

  const openModalRetencionParams = () => {
    setFormRetencionParams({
      porcentaje: String(retencionParams.porcentaje ?? '3'),
      monto_minimo_factura: String(retencionParams.monto_minimo_factura ?? '700')
    });
    setShowModalRetencionParams(true);
  };

  const handleSaveRetencionParams = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await depositosApi.updateRetencionProveedorParametros({
        porcentaje: parseFloat(formRetencionParams.porcentaje),
        monto_minimo_factura: parseFloat(formRetencionParams.monto_minimo_factura)
      });
      setRetencionParams({
        porcentaje: data.porcentaje,
        monto_minimo_factura: data.monto_minimo_factura
      });
      toast.success('Parámetros de retención actualizados');
      setShowModalRetencionParams(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar parámetros');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (formData.beneficiario_id) {
      loadCuentasBancarias(formData.beneficiario_id);
    } else {
      setCuentasBancarias([]);
    }
  }, [formData.beneficiario_id]);

  const loadTiposDeposito = async () => {
    try {
      const response = await depositosApi.getTiposDeposito(true);
      if (response.data) {
        setTiposDeposito(response.data);
      }
    } catch (error) {
      console.error('Error al cargar tipos de depósito:', error);
      toast.error('Error al cargar tipos de depósito');
    }
  };

  const loadCentrosCosto = async () => {
    try {
      const response = await depositosApi.getCentrosCosto(true);
      const list = Array.isArray(response.data) ? response.data : [];
      setCentrosCosto(list);
      if (list.length === 0) {
        toast.warning('No hay centros de costo activos. Créalos en Corporativo → Centros de costo.');
      }
    } catch (error) {
      console.error('Error al cargar centros de costo:', error);
      toast.error('Error al cargar centros de costo');
      setCentrosCosto([]);
    }
  };

  const loadBeneficiarios = async () => {
    try {
      const response = await depositosApi.getBeneficiarios(null, true);
      if (response.data) {
        setBeneficiarios(response.data);
      }
    } catch (error) {
      console.error('Error al cargar beneficiarios:', error);
    }
  };

  const loadBancos = async () => {
    try {
      const response = await bancosApi.getBancos(true); // Solo activos
      if (response.data && response.data.data) {
        setBancos(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar bancos:', error);
    }
  };

  const loadMetodosPago = async () => {
    setLoadingMetodosPago(true);
    try {
      console.log('📋 [Depositos] Iniciando carga de métodos de pago...');
      const response = await metodosPagoApi.getMetodosPago(true);
      console.log('📋 [Depositos] Respuesta completa:', response);
      console.log('📋 [Depositos] response.data:', response.data);
      
      let metodos = [];
      
      // ✅ Manejar diferentes estructuras de respuesta
      if (Array.isArray(response.data)) {
        metodos = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        metodos = response.data.data;
      } else if (response.data?.items && Array.isArray(response.data.items)) {
        metodos = response.data.items;
      } else if (response.data?.results && Array.isArray(response.data.results)) {
        metodos = response.data.results;
      } else if (response.data?.datos && Array.isArray(response.data.datos)) {
        metodos = response.data.datos;
      }
      
      console.log('✅ [Depositos] Métodos de pago procesados:', metodos.length);
      setMetodosPago(metodos);
      
      if (metodos.length === 0) {
        console.warn('⚠️ [Depositos] No hay métodos de pago disponibles');
      }
    } catch (error) {
      console.error('❌ [Depositos] Error al cargar métodos de pago:', error);
      console.error('❌ [Depositos] Error response:', error.response?.data);
      setMetodosPago([]);
      toast.error('Error al cargar métodos de pago');
    } finally {
      setLoadingMetodosPago(false);
    }
  };

  const loadTiposCuentaBancaria = async () => {
    try {
      const response = await depositosApi.getTiposCuentaBancaria(true);
      if (response.data) {
        const tipos = Array.isArray(response.data) ? response.data : response.data.data || [];
        setTiposCuentaBancaria(tipos);
      }
    } catch (error) {
      console.error('Error al cargar tipos de cuenta bancaria:', error);
      toast.error('Error al cargar tipos de cuenta bancaria');
    }
  };

  const loadCuentasBancarias = async (beneficiarioId) => {
    try {
      const response = await depositosApi.getCuentasByBeneficiario(beneficiarioId, true);
      if (response.data) {
        setCuentasBancarias(response.data);
      }
    } catch (error) {
      console.error('Error al cargar cuentas bancarias:', error);
      setCuentasBancarias([]);
    }
  };

  const loadDepositos = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        ...filters
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await depositosApi.getDepositos(params);
      if (response.data?.success) {
        setDepositos(response.data.data || []);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error('Error al cargar depósitos:', error);
      toast.error('Error al cargar depósitos');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    loadDepositos();
  }, [loadDepositos]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      // Si cambia el beneficiario, limpiar la cuenta bancaria
      if (name === 'beneficiario_id') {
        newData.cuenta_bancaria_id = '';
      }
      return newData;
    });
  };
  
  const handleBeneficiarioInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormDataBeneficiario(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleCuentaTemporalInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCuentaTemporal(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const agregarCuentaBancaria = () => {
    // Validar campos obligatorios
    if (!cuentaTemporal.banco || !cuentaTemporal.numero_cuenta || !cuentaTemporal.tipo_cuenta_bancaria_id) {
      toast.error('Debe completar todos los campos obligatorios de la cuenta');
      return;
    }
    
    // Obtener el tipo de cuenta seleccionado para validar si requiere código
    const tipoSeleccionado = tiposCuentaBancaria.find(t => t.id === parseInt(cuentaTemporal.tipo_cuenta_bancaria_id));
    if (tipoSeleccionado && tipoSeleccionado.requiere_codigo && !cuentaTemporal.codigo_cuenta) {
      toast.error('El tipo de cuenta seleccionado requiere un código');
      return;
    }
    
    // Agregar la cuenta a la lista
    setFormDataBeneficiario(prev => ({
      ...prev,
      cuentas_bancarias: [...prev.cuentas_bancarias, { ...cuentaTemporal }]
    }));
    
    // Limpiar el formulario temporal
    setCuentaTemporal({
      banco: '',
      numero_cuenta: '',
      tipo_cuenta_bancaria_id: '',
      codigo_cuenta: '',
      moneda: '',
      es_principal: false
    });
    
    toast.success('Cuenta agregada a la lista');
  };
  
  const eliminarCuentaBancaria = (index) => {
    setFormDataBeneficiario(prev => ({
      ...prev,
      cuentas_bancarias: prev.cuentas_bancarias.filter((_, i) => i !== index)
    }));
    toast.info('Cuenta eliminada de la lista');
  };
  
  const handleCuentaInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormDataCuenta(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(1); // Resetear a primera página al cambiar filtros
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (modoDeposito === 'con_beneficiario') {
      if (!String(formData.beneficiario_id || '').trim()) {
        toast.error('Seleccione un beneficiario de la lista de sugerencias');
        return;
      }
      if (!String(formData.cuenta_bancaria_id || '').trim()) {
        toast.error('Seleccione la cuenta bancaria del beneficiario');
        return;
      }
    }
    setLoading(true);

    try {
      const grossMonto = parseFloat(formData.monto);
      const fechaSolicitud =
        formData.fecha_solicitud && String(formData.fecha_solicitud).trim()
          ? String(formData.fecha_solicitud).trim().slice(0, 10)
          : defaultFechaSolicitud();
      const dataToSend = {
        ...formData,
        centro_costo_id: parseInt(formData.centro_costo_id, 10),
        tipo_deposito_id: parseInt(formData.tipo_deposito_id),
        beneficiario_id: formData.beneficiario_id ? parseInt(formData.beneficiario_id) : null,
        cuenta_bancaria_id: formData.cuenta_bancaria_id ? parseInt(formData.cuenta_bancaria_id) : null,
        monto: grossMonto,
        fecha_solicitud: fechaSolicitud
      };
      if (modoDeposito === 'con_beneficiario' && aplicaVistaRetencionProveedor()) {
        dataToSend.monto_factura = grossMonto;
      }
      
      // Si es creación (no edición), no enviar numero_documento (se generará automáticamente)
      if (!editingDeposito) {
        delete dataToSend.numero_documento;
      }
      
      // Remover campos vacíos (fecha_solicitud siempre va informada)
      Object.keys(dataToSend).forEach(key => {
        if (key === 'fecha_solicitud') return;
        if (dataToSend[key] === '' || dataToSend[key] === null) {
          delete dataToSend[key];
        }
      });

      if (modoDeposito === 'sin_destinatario') {
        dataToSend.beneficiario_id = null;
        dataToSend.cuenta_bancaria_id = null;
        dataToSend.beneficiario = null;
        dataToSend.banco = null;
        dataToSend.cuenta_bancaria = null;
        delete dataToSend.monto_factura;
        delete dataToSend.tipo_cuenta_bancaria_id;
        delete dataToSend.codigo_cuenta;
      }

      if (editingDeposito) {
        await depositosApi.updateDeposito(editingDeposito.id, dataToSend);
        toast.success('Depósito actualizado correctamente');
      } else {
        await depositosApi.createDeposito(dataToSend);
        toast.success('Depósito creado correctamente');
      }

      setShowModal(false);
      resetForm();
      loadDepositos();
    } catch (error) {
      console.error('Error al guardar depósito:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar depósito');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (deposito) => {
    setEditingDeposito(deposito);
    const montoEdicion =
      deposito.monto_factura != null && deposito.monto_factura !== ''
        ? deposito.monto_factura
        : deposito.monto;
    setFormData({
      centro_costo_id: deposito.centro_costo_id?.toString() || '',
      tipo_deposito_id: deposito.tipo_deposito_id?.toString() || '',
      beneficiario_id: deposito.beneficiario_id?.toString() || '',
      cuenta_bancaria_id: deposito.cuenta_bancaria_id?.toString() || '',
      numero_documento: deposito.numero_documento || '',
      monto: montoEdicion != null ? String(montoEdicion) : '',
      moneda: deposito.moneda || 'PEN',
      descripcion: deposito.descripcion || '',
      beneficiario: deposito.beneficiario || '',
      numero_operacion: deposito.numero_operacion || '',
      banco: deposito.banco || '',
      cuenta_bancaria: deposito.cuenta_bancaria || '',
      observaciones: deposito.observaciones || '',
      fecha_solicitud: toInputDate(deposito.fecha_solicitud)
    });
    if (deposito.beneficiario_id) {
      setModoDeposito('con_beneficiario');
      loadCuentasBancarias(deposito.beneficiario_id);
      const b = beneficiarios.find((x) => String(x.id) === String(deposito.beneficiario_id));
      setBeneficiarioBusqueda(
        b
          ? etiquetaBeneficiarioLista(b)
          : (deposito.beneficiario_nombre || deposito.beneficiario || '')
      );
    } else if (
      (deposito.beneficiario && String(deposito.beneficiario).trim()) ||
      (deposito.banco && String(deposito.banco).trim()) ||
      (deposito.cuenta_bancaria && String(deposito.cuenta_bancaria).trim())
    ) {
      setModoDeposito('libre');
      setCuentasBancarias([]);
      setBeneficiarioBusqueda('');
    } else {
      setModoDeposito('sin_destinatario');
      setCuentasBancarias([]);
      setBeneficiarioBusqueda('');
    }
    setShowModal(true);
  };

  const handleDelete = async (depositoId) => {
    const confirmar = window.confirm(
      'El depósito cambiará a estado Eliminado y dejará de aparecer en el listado y en los reportes.\n\n' +
      '¿Desea continuar?'
    );
    
    if (!confirmar) {
      return;
    }

    try {
      await depositosApi.deleteDeposito(depositoId);
      toast.success('Depósito eliminado correctamente');
      loadDepositos();
    } catch (error) {
      console.error('Error al eliminar depósito:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar depósito');
    }
  };

  const handleAprobar = async (depositoId) => {
    try {
      await depositosApi.aprobarDeposito(depositoId);
      toast.success('Depósito aprobado correctamente');
      loadDepositos();
    } catch (error) {
      console.error('Error al aprobar depósito:', error);
      toast.error(error.response?.data?.detail || 'Error al aprobar depósito');
    }
  };

  const tieneFechaPagoConfirmada = (d) => {
    const fp = d?.fecha_pago;
    if (fp == null || fp === '') return false;
    if (typeof fp === 'string' && !String(fp).trim()) return false;
    return true;
  };

  /** Igual que la etiqueta "Realizado": aprobado (o realizado) con fecha de pago; no cancelado */
  const esDepositoRealizado = (d) => {
    if (!d) return false;
    const cod = (d.estado || '').toLowerCase();
    if (cod === 'cancelado') return false;
    if (!tieneFechaPagoConfirmada(d)) return false;
    return cod === 'aprobado' || cod === 'realizado';
  };

  const abrirModalDesconfirmar = (deposito) => {
    setDepositoParaDesconfirmar(deposito);
    setShowModalDesconfirmar(true);
  };

  const cerrarModalDesconfirmar = () => {
    if (loading) return;
    setShowModalDesconfirmar(false);
    setDepositoParaDesconfirmar(null);
  };

  const ejecutarDesconfirmarDeposito = async () => {
    if (!depositoParaDesconfirmar) return;
    try {
      setLoading(true);
      await depositosApi.desmarcarDepositado(depositoParaDesconfirmar.id);
      toast.success('Depósito desconfirmado: quedó como Aprobado. Puede volver a registrar el pago y el comprobante.');
      setShowModalDesconfirmar(false);
      setDepositoParaDesconfirmar(null);
      loadDepositos();
    } catch (error) {
      console.error('Error al desconfirmar depósito:', error);
      toast.error(error.response?.data?.detail || 'Error al desconfirmar depósito');
    } finally {
      setLoading(false);
    }
  };

  const puedeDesaprobarDeposito = (d) =>
    Boolean(
      d &&
        canApprove &&
        (d.estado || '').toLowerCase() === 'aprobado' &&
        !tieneFechaPagoConfirmada(d)
    );

  const abrirModalDesaprobar = (deposito) => {
    setDepositoParaDesaprobar(deposito);
    setShowModalDesaprobar(true);
  };

  const cerrarModalDesaprobar = () => {
    if (loading) return;
    setShowModalDesaprobar(false);
    setDepositoParaDesaprobar(null);
  };

  const ejecutarDesaprobarDeposito = async () => {
    if (!depositoParaDesaprobar) return;
    try {
      setLoading(true);
      await depositosApi.desaprobarDeposito(depositoParaDesaprobar.id);
      toast.success('Depósito desaprobado: volvió a Solicitado. Ya puede editar el registro.');
      setShowModalDesaprobar(false);
      setDepositoParaDesaprobar(null);
      loadDepositos();
    } catch (error) {
      console.error('Error al desaprobar depósito:', error);
      toast.error(error.response?.data?.detail || 'Error al desaprobar depósito');
    } finally {
      setLoading(false);
    }
  };

  const limpiarComprobanteConfirmacion = () => {
    if (comprobantePreviewUrlRef.current) {
      URL.revokeObjectURL(comprobantePreviewUrlRef.current);
      comprobantePreviewUrlRef.current = null;
    }
    setComprobanteConfirmacionFile(null);
    setComprobanteConfirmacionPreview(null);
  };

  const resetConfirmarDepositoModal = () => {
    limpiarComprobanteConfirmacion();
    setShowModalConfirmarDeposito(false);
    setDepositoParaConfirmar(null);
    setNumeroOperacionConfirmacion('');
    setMetodoPagoConfirmacion('');
  };

  const handleComprobanteConfirmacionChange = (e) => {
    const f = e.target.files?.[0];
    if (comprobantePreviewUrlRef.current) {
      URL.revokeObjectURL(comprobantePreviewUrlRef.current);
      comprobantePreviewUrlRef.current = null;
    }
    if (!f) {
      setComprobanteConfirmacionFile(null);
      setComprobanteConfirmacionPreview(null);
      return;
    }
    const url = URL.createObjectURL(f);
    comprobantePreviewUrlRef.current = url;
    setComprobanteConfirmacionPreview(url);
    setComprobanteConfirmacionFile(f);
  };

  const cerrarModalVerComprobante = () => {
    if (verComprobanteObjectUrl) {
      URL.revokeObjectURL(verComprobanteObjectUrl);
      setVerComprobanteObjectUrl(null);
    }
    setShowModalVerComprobante(false);
  };

  const abrirVerComprobanteDeposito = async (depositoId) => {
    cerrarModalVerComprobante();
    setLoadingVerComprobante(true);
    try {
      const res = await depositosApi.getComprobanteImagenDeposito(depositoId);
      const blob = res.data;
      if (blob.type && blob.type.includes('json')) {
        const text = await blob.text();
        let msg = 'No se pudo cargar el comprobante';
        try {
          const j = JSON.parse(text);
          msg = j.detail || j.message || msg;
        } catch {
          /* ignore */
        }
        toast.error(msg);
        return;
      }
      const url = URL.createObjectURL(blob);
      setVerComprobanteObjectUrl(url);
      setShowModalVerComprobante(true);
    } catch (error) {
      console.error('Error al cargar comprobante:', error);
      toast.error(error.response?.data?.detail || 'Error al cargar la imagen del comprobante');
    } finally {
      setLoadingVerComprobante(false);
    }
  };

  const handleConfirmarDeposito = async (deposito) => {
    console.log('🔍 [Depositos] handleConfirmarDeposito llamado');
    limpiarComprobanteConfirmacion();
    setDepositoParaConfirmar(deposito);
    setFechaPagoConfirmacion(new Date().toISOString().split('T')[0]);
    setNumeroOperacionConfirmacion('');
    setMetodoPagoConfirmacion('');
    
    // ✅ Siempre cargar métodos de pago antes de abrir el modal
    console.log('🔄 [Depositos] Cargando métodos de pago...');
    await loadMetodosPago();
    console.log('🔍 [Depositos] metodosPago después de cargar:', metodosPago?.length);
    
    setShowModalConfirmarDeposito(true);
    console.log('✅ [Depositos] Modal abierto');
  };

  const handleExportPDF = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters  // Usar los filtros actuales tal cual
      };
      
      // Remover filtros vacíos
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await depositosApi.exportPDF(params);
      
      // Crear blob y descargar
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `depositos_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF generado correctamente');
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      toast.error(error.response?.data?.detail || 'Error al generar PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcelResumen = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters  // Usar los filtros actuales tal cual
      };
      
      // Remover filtros vacíos
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await depositosApi.exportExcelResumen(params);
      
      // Crear blob y descargar
      const blob = new Blob([response.data], { 
        type: 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `depositos_resumen_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Resumen por Banco (PDF) generado correctamente');
    } catch (error) {
      console.error('Error al exportar PDF resumen:', error);
      toast.error(error.response?.data?.detail || 'Error al generar PDF resumen');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcelDetallado = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters  // Usar los filtros actuales tal cual
      };
      
      // Remover filtros vacíos
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await depositosApi.exportExcelDetallado(params);
      
      // Crear blob y descargar
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `depositos_detallado_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel detallado generado correctamente');
    } catch (error) {
      console.error('Error al exportar Excel detallado:', error);
      toast.error(error.response?.data?.detail || 'Error al generar Excel detallado');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarDepositoSubmit = async () => {
    if (!depositoParaConfirmar) return;

    if (!fechaPagoConfirmacion) {
      toast.error('Debe ingresar la fecha de pago');
      return;
    }

    if (!metodoPagoConfirmacion) {
      toast.error('Debe seleccionar un método de pago');
      return;
    }


    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('fecha_pago', fechaPagoConfirmacion);
      fd.append('metodo_pago', metodoPagoConfirmacion);
      fd.append('numero_operacion', numeroOperacionConfirmacion || '');
      if (comprobanteConfirmacionFile) {
        fd.append('comprobante', comprobanteConfirmacionFile);
      }
      await depositosApi.marcarDepositado(depositoParaConfirmar.id, fd);
      toast.success('Depósito confirmado correctamente');
      resetConfirmarDepositoModal();
      loadDepositos();
    } catch (error) {
      console.error('Error al confirmar depósito:', error);
      toast.error(error.response?.data?.detail || 'Error al confirmar depósito');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBeneficiario = async (e) => {
    e.preventDefault();
    
    // Validar que haya al menos una cuenta bancaria
    if (formDataBeneficiario.cuentas_bancarias.length === 0) {
      toast.error('Debe agregar al menos una cuenta bancaria');
      return;
    }
    
    // Validar que al menos una cuenta sea principal
    const tienePrincipal = formDataBeneficiario.cuentas_bancarias.some(c => c.es_principal);
    if (!tienePrincipal) {
      toast.error('Debe marcar al menos una cuenta como principal');
      return;
    }
    
    setLoading(true);
    
    try {
      const dataToSend = {
        ...formDataBeneficiario,
        tipo_documento_id: formDataBeneficiario.tipo_documento_id ? parseInt(formDataBeneficiario.tipo_documento_id) : null
      };
      
      // Usar el endpoint que acepta cuentas bancarias
      await depositosApi.createBeneficiarioConCuentas(dataToSend);
      toast.success('Beneficiario y cuentas creados correctamente');
      setShowModalBeneficiario(false);
      setFormDataBeneficiario({
        tipo: 'proveedor',
        nombre: '',
        numero_documento: '',
        tipo_documento_id: '',
        contacto_nombre: '',
        contacto_email: '',
        contacto_telefono: '',
        direccion: '',
        excluido_retencion: false,
        cuentas_bancarias: []
      });
      loadBeneficiarios();
    } catch (error) {
      console.error('Error al crear beneficiario:', error);
      toast.error(error.response?.data?.detail || 'Error al crear beneficiario');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateCuenta = async (e) => {
    e.preventDefault();
    if (!formDataCuenta.beneficiario_id) {
      toast.error('Seleccione un beneficiario');
      return;
    }
    
    setLoading(true);
    
    try {
      const dataToSend = {
        ...formDataCuenta,
        beneficiario_id: parseInt(formDataCuenta.beneficiario_id)
      };
      
      await depositosApi.createCuentaBancaria(dataToSend);
      toast.success('Cuenta bancaria creada correctamente');
      setShowModalCuenta(false);
      setFormDataCuenta({
        beneficiario_id: '',
        banco: '',
        numero_cuenta: '',
        tipo_cuenta: '',
        codigo_cuenta: '',
        moneda: 'PEN',
        es_principal: false
      });
      // Recargar cuentas del beneficiario si está seleccionado en el formulario principal
      if (formData.beneficiario_id && formData.beneficiario_id === formDataCuenta.beneficiario_id) {
        loadCuentasBancarias(parseInt(formDataCuenta.beneficiario_id));
      }
      loadBeneficiarios();
    } catch (error) {
      console.error('Error al crear cuenta bancaria:', error);
      toast.error(error.response?.data?.detail || 'Error al crear cuenta bancaria');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingDeposito(null);
    setModoDeposito('con_beneficiario');
    setFormData({
      centro_costo_id: '',
      tipo_deposito_id: '',
      beneficiario_id: '',
      cuenta_bancaria_id: '',
      numero_documento: '',
      monto: '',
      moneda: 'PEN',
      descripcion: '',
      beneficiario: '',
      banco: '',
      cuenta_bancaria: '',
      tipo_cuenta_bancaria_id: '',
      codigo_cuenta: '',
      numero_operacion: '',
      observaciones: '',
      fecha_solicitud: defaultFechaSolicitud()
    });
    setCuentasBancarias([]);
    setBeneficiarioBusqueda('');
    setBeneficiarioSugerenciasAbiertas(false);
  };

  const etiquetaEstadoDeposito = (deposito) => {
    if (!deposito) return '';
    const cod = (deposito.estado || '').toLowerCase();
    if (cod === 'aprobado' && deposito.fecha_pago) {
      const s = (deposito.estado_nombre_si_pagado || '').trim();
      return s || 'Realizado';
    }
    const n = (deposito.estado_deposito_nombre || '').trim();
    return n || deposito.estado || '';
  };

  const getEstadoBadge = (deposito) => {
    const estado = deposito.estado;
    const label = etiquetaEstadoDeposito(deposito);
    if (estado === 'cancelado' || estado === 'eliminado') {
      return <Badge bg="danger">{label}</Badge>;
    }
    if (estado === 'registrado') {
      return <Badge bg="secondary">{label}</Badge>;
    }
    if (estado === 'aprobado') {
      return <Badge bg={deposito.fecha_pago ? 'info' : 'success'}>{label}</Badge>;
    }
    return <Badge bg="secondary">{label}</Badge>;
  };

  const renderModalDesconfirmarDeposito = () => {
    const d = depositoParaDesconfirmar;
    const fechaPagoTxt =
      d?.fecha_pago != null && String(d.fecha_pago).trim()
        ? String(d.fecha_pago).trim().slice(0, 10)
        : '—';
    const metodoTxt = d?.metodo_pago && String(d.metodo_pago).trim() ? String(d.metodo_pago).trim() : '—';
    const numOpTxt =
      d?.numero_operacion && String(d.numero_operacion).trim() ? String(d.numero_operacion).trim() : '—';
    const tieneComprobante = Boolean(d?.comprobante_deposito_path && String(d.comprobante_deposito_path).trim());

    return (
      <Modal
        show={showModalDesconfirmar}
        onHide={cerrarModalDesconfirmar}
        centered
        size="lg"
        backdrop="static"
        keyboard={!loading}
      >
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>Desconfirmar depósito</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {d && (
            <>
              <Alert variant="warning" className="mb-3">
                <strong>Revise antes de continuar.</strong> Va a revertir la confirmación de pago de un depósito en
                estado <strong>Realizado</strong>. Los datos que figuran en la siguiente tabla se{' '}
                <strong>eliminarán o dejarán en blanco</strong> en el sistema; el comprobante adjunto, si existe, se
                quitará del servidor.
              </Alert>

              <Card className="mb-3 shadow-sm">
                <Card.Header as="h6" className="py-2 bg-light mb-0">
                  Depósito y contexto
                </Card.Header>
                <Card.Body className="py-3">
                  <Table borderless size="sm" className="mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted" style={{ width: '38%' }}>
                          ID
                        </td>
                        <td>
                          <strong>#{d.id}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Estado actual en pantalla</td>
                        <td>
                          <Badge bg="info">{etiquetaEstadoDeposito(d)}</Badge>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Centro de costo</td>
                        <td>{d.centro_costo_nombre || '—'}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Beneficiario</td>
                        <td>{d.beneficiario_nombre || d.beneficiario || '—'}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Monto neto a depositar</td>
                        <td>
                          <strong>
                            {d.moneda} {parseFloat(d.monto || 0).toFixed(2)}
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Descripción</td>
                        <td style={{ whiteSpace: 'pre-wrap' }}>{d.descripcion || '—'}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              <Card className="mb-3 border-danger">
                <Card.Header as="h6" className="py-2 bg-danger text-white mb-0">
                  Lo que se va a desconfirmar (se perderá en este depósito)
                </Card.Header>
                <Card.Body className="py-3">
                  <Table borderless size="sm" className="mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted" style={{ width: '38%' }}>
                          Fecha de pago
                        </td>
                        <td>
                          <strong>{fechaPagoTxt}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Método de pago</td>
                        <td>{metodoTxt}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Número de operación</td>
                        <td>{numOpTxt}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Imagen del comprobante</td>
                        <td>
                          {tieneComprobante ? (
                            <span className="text-danger">
                              <strong>Sí</strong> — se eliminará la imagen guardada
                            </span>
                          ) : (
                            <span className="text-muted">No hay imagen adjunta</span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              <Alert variant="secondary" className="mb-0 small">
                Después de desconfirmar, el depósito volverá a mostrarse como <strong>Aprobado</strong> (sin fecha de
                pago). Podrá usar <strong>Confirmar</strong> de nuevo para registrar fecha, método, operación y un
                comprobante nuevos.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button variant="outline-secondary" onClick={cerrarModalDesconfirmar} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={ejecutarDesconfirmarDeposito} disabled={loading || !d}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Procesando…
              </>
            ) : (
              'Sí, desconfirmar depósito'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const renderModalDesaprobarDeposito = () => {
    const d = depositoParaDesaprobar;
    return (
      <Modal
        show={showModalDesaprobar}
        onHide={cerrarModalDesaprobar}
        centered
        backdrop="static"
        keyboard={!loading}
      >
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>Desaprobar depósito</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {d && (
            <>
              <Alert variant="warning" className="mb-3">
                <strong>Revise antes de continuar.</strong> Va a <strong>revocar la aprobación</strong> de este
                depósito. Pasará al estado <strong>Solicitado</strong> (registrado), se limpiarán los datos de quién
                aprobó y la fecha de aprobación, y <strong>podrá editar</strong> la solicitud de nuevo (montos,
                beneficiario, descripción, etc.).
              </Alert>
              <Alert variant="info" className="small py-2 mb-3">
                No use esta opción si el depósito ya está <strong>Realizado</strong> (con fecha de pago). En ese caso
                desconfirme el pago primero.
              </Alert>
              <Card className="mb-0 shadow-sm">
                <Card.Header as="h6" className="py-2 bg-light mb-0">
                  Depósito afectado
                </Card.Header>
                <Card.Body className="py-3">
                  <Table borderless size="sm" className="mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted" style={{ width: '38%' }}>
                          ID
                        </td>
                        <td>
                          <strong>#{d.id}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Estado actual</td>
                        <td>
                          <Badge bg="success">{etiquetaEstadoDeposito(d)}</Badge>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Centro de costo</td>
                        <td>{d.centro_costo_nombre || '—'}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Beneficiario</td>
                        <td>{d.beneficiario_nombre || d.beneficiario || '—'}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Monto neto</td>
                        <td>
                          <strong>
                            {d.moneda} {parseFloat(d.monto || 0).toFixed(2)}
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Descripción</td>
                        <td style={{ whiteSpace: 'pre-wrap' }}>{d.descripcion || '—'}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
              <Alert variant="secondary" className="mt-3 mb-0 small">
                Después de desaprobar deberá <strong>aprobar de nuevo</strong> cuando la solicitud esté corregida.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button variant="outline-secondary" onClick={cerrarModalDesaprobar} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="warning" onClick={ejecutarDesaprobarDeposito} disabled={loading || !d}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Procesando…
              </>
            ) : (
              'Sí, desaprobar y permitir edición'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // Vista móvil optimizada
  if (isMobile) {
    return (
      <Container fluid className="p-2">
        <ToastContainer />
        <style>
          {`
            .mobile-deposit-card {
              border-radius: 12px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              margin-bottom: 12px;
              border-left: 4px solid #667eea;
            }
            .mobile-header {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              padding: 12px;
              border-radius: 8px;
              margin-bottom: 16px;
            }
            .mobile-filter-btn {
              width: 100%;
              margin-bottom: 8px;
            }
            .mobile-action-btn {
              width: 100%;
              margin-bottom: 8px;
            }
            .deposit-amount {
              font-size: 1.2rem;
              font-weight: bold;
              color: #667eea;
            }
            .deposit-info-label {
              font-size: 0.75rem;
              color: #6c757d;
              text-transform: uppercase;
            }
            .deposit-info-value {
              font-size: 0.95rem;
              font-weight: 500;
            }
          `}
        </style>

        <div className="mobile-header">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <FaMoneyBillWave size={24} className="me-2" />
              <strong>Solicitudes de Depósito</strong>
            </div>
            <Badge bg="light" text="dark">{depositos.length}</Badge>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="mb-3">
          <Button
            variant="primary"
            className="mobile-action-btn"
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
          >
            <FaFilter className="me-2" />
            {mostrarFiltros ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </Button>
          
          <Button
            variant="success"
            className="mobile-action-btn"
            onClick={handleExportPDF}
            disabled={loading}
          >
            <FaFilePdf className="me-2" />
            Exportar PDF
          </Button>

          <Button
            variant="info"
            className="mobile-action-btn"
            onClick={handleExportExcelResumen}
            disabled={loading}
          >
            <FaFilePdf className="me-2" />
            Resumen por Banco
          </Button>

          <Button
            variant="primary"
            className="mobile-action-btn"
            onClick={handleExportExcelDetallado}
            disabled={loading}
          >
            <FaFileExcel className="me-2" />
            Excel Detallado
          </Button>
          
          <Button
            variant="light"
            className="mobile-action-btn"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <FaPlus className="me-2" />
            Nueva Solicitud
          </Button>
          <Button
            variant="outline-light"
            className="mobile-action-btn"
            style={{ borderColor: '#fff', color: '#fff' }}
            onClick={openModalRetencionParams}
          >
            <FaCog className="me-2" />
            Parámetros retención
          </Button>
        </div>

        {/* Filtros colapsables */}
        {mostrarFiltros && (
          <Card className="mb-3">
            <Card.Body>
              <Form.Group className="mb-2">
                <Form.Label>Centro de costo</Form.Label>
                <Form.Select
                  name="centro_costo_id"
                  value={filters.centro_costo_id}
                  onChange={handleFilterChange}
                  size="sm"
                >
                  <option value="">Todos</option>
                  {centrosCosto.map((cc) => (
                    <option key={cc.id} value={cc.id}>
                      {cc.codigo} — {cc.nombre}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-2">
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  name="estado"
                  value={filters.estado}
                  onChange={handleFilterChange}
                  size="sm"
                >
                  <option value="">Todos</option>
                  <option value="registrado">Registrado</option>
                  <option value="aprobado">Aprobado</option>
                  <option value="cancelado">Cancelado</option>
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-2">
                <Form.Label>Tipo</Form.Label>
                <Form.Select
                  name="tipo_deposito_id"
                  value={filters.tipo_deposito_id}
                  onChange={handleFilterChange}
                  size="sm"
                >
                  <option value="">Todos</option>
                  {tiposDeposito.map(tipo => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.nombre}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>
        )}

        {/* Lista de depósitos */}
        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Cargando...</p>
          </div>
        ) : depositos.length === 0 ? (
          <Alert variant="info">No se encontraron solicitudes</Alert>
        ) : (
          <>
            {depositos.map(deposito => (
              <Card key={deposito.id} className="mobile-deposit-card">
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <div className="deposit-info-label">Beneficiario</div>
                      <div className="deposit-info-value">
                        {deposito.beneficiario_nombre || deposito.beneficiario || '-'}
                      </div>
                    </div>
                    <div>
                      {getEstadoBadge(deposito)}
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <div className="deposit-info-label">Monto neto a depositar</div>
                    <div className="deposit-amount">
                      {deposito.moneda} {parseFloat(deposito.monto).toFixed(2)}
                    </div>
                    {Number(deposito.monto_retencion) > 0 && deposito.monto_factura != null && (
                      <small className="text-muted d-block mt-1">
                        Factura {parseFloat(deposito.monto_factura).toFixed(2)} — Retención{' '}
                        {parseFloat(deposito.monto_retencion).toFixed(2)}
                      </small>
                    )}
                  </div>
                  
                  <div className="mb-2">
                    <div className="deposit-info-label">Descripción</div>
                    <div className="deposit-info-value" style={{fontSize: '0.85rem'}}>
                      {deposito.descripcion || '-'}
                    </div>
                  </div>
                  
                  <Row className="mb-2">
                    <Col xs={6}>
                      <div className="deposit-info-label">Centro de costo</div>
                      <div className="deposit-info-value" style={{fontSize: '0.85rem'}}>
                        {deposito.centro_costo_nombre || '-'}
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="deposit-info-label">Tipo</div>
                      <div className="deposit-info-value" style={{fontSize: '0.85rem'}}>
                        {deposito.tipo_deposito_nombre || '-'}
                      </div>
                    </Col>
                  </Row>
                  
                  <Row className="mb-2">
                    <Col xs={6}>
                      <div className="deposit-info-label">Fecha Solicitud</div>
                      <div className="deposit-info-value" style={{fontSize: '0.85rem'}}>
                        {deposito.fecha_solicitud || '-'}
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="deposit-info-label">Fecha Pago</div>
                      <div className="deposit-info-value" style={{fontSize: '0.85rem'}}>
                        {deposito.fecha_pago || '-'}
                      </div>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={12}>
                      <div className="deposit-info-label">Registro (auditoría)</div>
                      <div className="deposit-info-value" style={{ fontSize: '0.85rem' }}>
                        {formatAuditoriaCreacion(deposito.created_at)}
                      </div>
                    </Col>
                  </Row>
                  
                  {/* Botones de acción */}
                  <div className="d-flex gap-2 mt-3 flex-wrap">
                    {deposito.estado === 'registrado' && canApprove && (
                      <Button
                        variant="success"
                        size="sm"
                        className="flex-fill"
                        onClick={() => handleAprobar(deposito.id)}
                      >
                        <FaCheck className="me-1" />
                        Aprobar
                      </Button>
                    )}
                    {deposito.estado === 'aprobado' && !tieneFechaPagoConfirmada(deposito) && (
                      <Button
                        variant="primary"
                        size="sm"
                        className="flex-fill"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleConfirmarDeposito(deposito);
                        }}
                      >
                        <FaCheckCircle className="me-1" />
                        Confirmar
                      </Button>
                    )}
                    {puedeDesaprobarDeposito(deposito) && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="flex-fill"
                        onClick={() => abrirModalDesaprobar(deposito)}
                        disabled={loading}
                        title="Revocar aprobación: vuelve a Solicitado y permite editar"
                      >
                        <FaReply className="me-1" />
                        Desaprobar
                      </Button>
                    )}
                    {esDepositoRealizado(deposito) && (
                      <Button
                        variant="outline-warning"
                        size="sm"
                        className="flex-fill"
                        onClick={() => abrirModalDesconfirmar(deposito)}
                        disabled={loading}
                        title="Desconfirmar depósito Realizado: vuelve a Aprobado para corregir fecha o comprobante"
                      >
                        <FaUndo className="me-1" />
                        Desconfirmar
                      </Button>
                    )}
                    {deposito.comprobante_deposito_path && (
                      <Button
                        variant="outline-info"
                        size="sm"
                        className="flex-fill"
                        onClick={() => abrirVerComprobanteDeposito(deposito.id)}
                        disabled={loadingVerComprobante}
                        title="Ver imagen del comprobante"
                      >
                        <FaImage className="me-1" />
                        Comprobante
                      </Button>
                    )}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleEdit(deposito)}
                      disabled={deposito.estado === 'aprobado' || deposito.estado === 'cancelado'}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(deposito.id)}
                      disabled={deposito.estado === 'eliminado'}
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            ))}
            
            {/* Paginación móvil */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Anterior
              </Button>
              <span style={{fontSize: '0.9rem'}}>Página {page} de {Math.ceil(total / pageSize)}</span>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page * pageSize >= total}
              >
                Siguiente
              </Button>
            </div>
          </>
        )}

        {/* Los modales se mantienen igual */}
        {/* Modal para crear/editar depósito */}
        <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="lg" fullscreen="sm-down">
          <Modal.Header closeButton>
            <Modal.Title style={{fontSize: '1.1rem'}}>
              {editingDeposito ? 'Editar Solicitud' : 'Nueva Solicitud'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Centro de costo *</Form.Label>
                    <Form.Select
                      name="centro_costo_id"
                      value={formData.centro_costo_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Seleccione un centro de costo</option>
                      {centrosCosto.map((cc) => (
                        <option key={cc.id} value={cc.id}>
                          {cc.codigo} — {cc.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tipo de Depósito *</Form.Label>
                    <Form.Select
                      name="tipo_deposito_id"
                      value={formData.tipo_deposito_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Seleccione un tipo</option>
                      {tiposDeposito.map(tipo => (
                        <option key={tipo.id} value={tipo.id}>
                          {tipo.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fecha de solicitud *</Form.Label>
                    <Form.Control
                      type="date"
                      name="fecha_solicitud"
                      value={formData.fecha_solicitud}
                      onChange={handleInputChange}
                      required
                    />
                    <Form.Text className="text-muted">Por defecto hoy; puede cambiarla.</Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {editingDeposito && (
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Número de Documento</Form.Label>
                      <Form.Control
                        type="text"
                        name="numero_documento"
                        value={formData.numero_documento}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {aplicaVistaRetencionProveedor()
                        ? `Monto total factura (${formData.moneda || 'PEN'}) *`
                        : 'Monto *'}
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      name="monto"
                      value={formData.monto}
                      onChange={handleInputChange}
                      required
                      min="0.01"
                    />
                    {(() => {
                      const pr = getRetencionPreview();
                      if (!pr.muestra) return null;
                      if (pr.sinRetPorMinimo) {
                        return (
                          <Alert variant="info" className="mt-2 py-2 mb-0" style={{ fontSize: '0.85rem' }}>
                            No aplica retención: el monto no supera {simboloMonedaRetencion(formData.moneda)}{' '}
                            {Number(retencionParams.monto_minimo_factura).toFixed(2)} (mismo umbral en{' '}
                            {formData.moneda || 'PEN'}, según parámetros).
                          </Alert>
                        );
                      }
                      if (pr.retencion > 0) {
                        return (
                          <Alert variant="warning" className="mt-2 py-2 mb-0" style={{ fontSize: '0.85rem' }}>
                            Retención ({retencionParams.porcentaje}%): {simboloMonedaRetencion(formData.moneda)}{' '}
                            {pr.retencion.toFixed(2)} — Monto a depositar (neto):{' '}
                            {simboloMonedaRetencion(formData.moneda)}
                            {pr.neto.toFixed(2)}
                          </Alert>
                        );
                      }
                      return null;
                    })()}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Moneda *</Form.Label>
                    <Form.Select
                      name="moneda"
                      value={formData.moneda}
                      onChange={handleInputChange}
                    >
                      <option value="PEN">PEN (Soles)</option>
                      <option value="USD">USD (Dólares)</option>
                      <option value="EUR">EUR (Euros)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <hr className="my-3" />

              {/* Selector de modo de depósito */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">Tipo de Registro *</Form.Label>
                <div className="d-flex flex-column gap-2">
                  <Form.Check
                    type="radio"
                    id="modo-con-beneficiario-mobile"
                    name="modo_deposito"
                    label="Con Beneficiario Registrado"
                    checked={modoDeposito === 'con_beneficiario'}
                    onChange={() => setModoDeposito('con_beneficiario')}
                  />
                  <Form.Check
                    type="radio"
                    id="modo-libre-mobile"
                    name="modo_deposito"
                    label="Ingreso Libre (sin registrar)"
                    checked={modoDeposito === 'libre'}
                    onChange={() => setModoDeposito('libre')}
                  />
                  <Form.Check
                    type="radio"
                    id="modo-sin-dest-mobile"
                    name="modo_deposito"
                    label="Registro sin beneficiario ni cuenta (temporal)"
                    checked={modoDeposito === 'sin_destinatario'}
                    onChange={() => setModoDeposito('sin_destinatario')}
                  />
                </div>
                {modoDeposito === 'sin_destinatario' && (
                  <Alert variant="warning" className="mt-2 py-2 mb-0" style={{ fontSize: '0.85rem' }}>
                    Permite cargar monto, centro de costo y tipo de depósito sin asignar beneficiario ni cuenta. Use esta
                    opción de forma temporal; los datos del destinatario se podrán completar después según el flujo que
                    defina la organización.
                  </Alert>
                )}
              </Form.Group>

              {/* Modo: Con Beneficiario */}
              {modoDeposito === 'con_beneficiario' && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Beneficiario / proveedor *</Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type="text"
                        autoComplete="off"
                        placeholder="Buscar por nombre…"
                        value={beneficiarioBusqueda}
                        onChange={handleBeneficiarioBusquedaChange}
                        onFocus={() => setBeneficiarioSugerenciasAbiertas(true)}
                        onBlur={() => {
                          beneficiarioBlurTimerRef.current = setTimeout(() => {
                            setBeneficiarioSugerenciasAbiertas(false);
                          }, 200);
                        }}
                      />
                      {beneficiarioSugerenciasAbiertas && beneficiarioBusqueda.trim() && (
                        <>
                          {beneficiariosFiltrados.length > 0 ? (
                            <ListGroup
                              className="position-absolute w-100 shadow-sm mt-1 rounded"
                              style={{ zIndex: 1060, maxHeight: 240, overflowY: 'auto' }}
                            >
                              {beneficiariosFiltrados.map((b) => (
                                <ListGroup.Item
                                  key={b.id}
                                  action
                                  as="button"
                                  type="button"
                                  className="py-2 text-start"
                                  onMouseDown={(ev) => ev.preventDefault()}
                                  onClick={() => seleccionarBeneficiario(b)}
                                >
                                  {etiquetaBeneficiarioLista(b)}
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          ) : (
                            <div
                              className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 px-2 py-2 small text-muted"
                              style={{ zIndex: 1060 }}
                            >
                              Sin coincidencias
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <Form.Text muted>Escriba para filtrar y elija una fila de la lista.</Form.Text>
                  </Form.Group>
                  
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="w-100 mb-3"
                    onClick={() => setShowModalBeneficiario(true)}
                  >
                    <FaPlus /> Nuevo Beneficiario
                  </Button>

                  {formData.beneficiario_id && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Cuenta Bancaria *</Form.Label>
                        <Form.Select
                          name="cuenta_bancaria_id"
                          value={formData.cuenta_bancaria_id}
                          onChange={handleInputChange}
                          required={modoDeposito === 'con_beneficiario' && formData.beneficiario_id}
                        >
                          <option value="">Seleccione...</option>
                          {cuentasBancarias.map(cuenta => (
                            <option key={cuenta.id} value={cuenta.id}>
                              {cuenta.banco} - {cuenta.numero_cuenta}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      
                      <Button
                        variant="outline-success"
                        size="sm"
                        className="w-100 mb-3"
                        onClick={() => {
                          setFormDataCuenta(prev => ({ ...prev, beneficiario_id: formData.beneficiario_id }));
                          setShowModalCuenta(true);
                        }}
                        disabled={!formData.beneficiario_id}
                      >
                        <FaPlus /> Nueva Cuenta
                      </Button>
                    </>
                  )}
                </>
              )}

              {/* Modo: Libre */}
              {modoDeposito === 'libre' && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Nombre del Beneficiario *</Form.Label>
                    <Form.Control
                      type="text"
                      name="beneficiario"
                      value={formData.beneficiario}
                      onChange={handleInputChange}
                      placeholder="Ingrese el nombre"
                      required={modoDeposito === 'libre'}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Tipo de Cuenta *</Form.Label>
                    <Form.Select
                      name="tipo_cuenta_bancaria_id"
                      value={formData.tipo_cuenta_bancaria_id}
                      onChange={handleInputChange}
                      required={modoDeposito === 'libre'}
                    >
                      <option value="">Seleccione...</option>
                      {tiposCuentaBancaria.map(tipo => (
                        <option key={tipo.id} value={tipo.id}>
                          {tipo.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Banco *</Form.Label>
                    <Form.Select
                      name="banco"
                      value={formData.banco}
                      onChange={handleInputChange}
                      required={modoDeposito === 'libre'}
                    >
                      <option value="">Seleccione...</option>
                      {bancos.map(banco => (
                        <option key={banco.id} value={banco.nombre}>
                          {banco.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Número de Cuenta *</Form.Label>
                    <Form.Control
                      type="text"
                      name="cuenta_bancaria"
                      value={formData.cuenta_bancaria}
                      onChange={handleInputChange}
                      placeholder="Ej: 123456789"
                      required={modoDeposito === 'libre'}
                    />
                  </Form.Group>
                  
                  {(() => {
                    const tipoSeleccionado = tiposCuentaBancaria.find(t => t.id === parseInt(formData.tipo_cuenta_bancaria_id));
                    return tipoSeleccionado?.requiere_codigo && (
                      <Form.Group className="mb-3">
                        <Form.Label>Código *</Form.Label>
                        <Form.Control
                          type="text"
                          name="codigo_cuenta"
                          value={formData.codigo_cuenta}
                          onChange={handleInputChange}
                          placeholder="Código recaudadora"
                          required
                        />
                      </Form.Group>
                    );
                  })()}
                </>
              )}

              <hr className="my-3" />

              <Form.Group className="mb-3">
                <Form.Label>Descripción *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleInputChange}
                  placeholder="Motivo del depósito"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Observaciones</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleInputChange}
                  placeholder="Opcional"
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? <Spinner size="sm" /> : (editingDeposito ? 'Actualizar' : 'Crear')}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Modal para crear beneficiario */}
        <Modal 
          show={showModalBeneficiario} 
          onHide={() => { setShowModalBeneficiario(false); setMostrarDatosContacto(false); }} 
          size="lg"
          fullscreen="sm-down"
        >
          <Modal.Header closeButton>
            <Modal.Title style={{fontSize: '1.1rem'}}>Nuevo Beneficiario</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleCreateBeneficiario}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Tipo *</Form.Label>
                <Form.Select
                  name="tipo"
                  value={formDataBeneficiario.tipo}
                  onChange={handleBeneficiarioInputChange}
                  required
                >
                  <option value="proveedor">Proveedor</option>
                  <option value="beneficiario">Beneficiario</option>
                  <option value="servicio">Servicio</option>
                </Form.Select>
              </Form.Group>
              {formDataBeneficiario.tipo === 'proveedor' && (
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="excluido_retencion"
                    label="Proveedor excluido de retención (agente de retención / no sujeto)"
                    checked={!!formDataBeneficiario.excluido_retencion}
                    onChange={handleBeneficiarioInputChange}
                  />
                </Form.Group>
              )}
              
              <Form.Group className="mb-3">
                <Form.Label>Nombre *</Form.Label>
                <Form.Control
                  type="text"
                  name="nombre"
                  value={formDataBeneficiario.nombre}
                  onChange={handleBeneficiarioInputChange}
                  required
                  placeholder="Nombre completo"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Documento</Form.Label>
                <Form.Control
                  type="text"
                  name="numero_documento"
                  value={formDataBeneficiario.numero_documento}
                  onChange={handleBeneficiarioInputChange}
                  placeholder="RUC, DNI, etc."
                />
              </Form.Group>
              
              <hr className="my-3" />
              
              <h6 className="mb-3">Cuentas Bancarias *</h6>
              <Card className="mb-3" style={{backgroundColor: '#f8f9fa'}}>
                <Card.Body>
                  <Form.Group className="mb-2">
                    <Form.Label>Tipo *</Form.Label>
                    <Form.Select
                      name="tipo_cuenta_bancaria_id"
                      value={cuentaTemporal.tipo_cuenta_bancaria_id}
                      onChange={handleCuentaTemporalInputChange}
                      size="sm"
                    >
                      <option value="">Seleccione...</option>
                      {tiposCuentaBancaria.map(tipo => (
                        <option key={tipo.id} value={tipo.id}>
                          {tipo.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  
                  <Form.Group className="mb-2">
                    <Form.Label>Banco *</Form.Label>
                    <Form.Select
                      name="banco"
                      value={cuentaTemporal.banco}
                      onChange={handleCuentaTemporalInputChange}
                      size="sm"
                    >
                      <option value="">Seleccione...</option>
                      {bancos.map(banco => (
                        <option key={banco.id} value={banco.nombre}>
                          {banco.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  
                  <Form.Group className="mb-2">
                    <Form.Label>Número *</Form.Label>
                    <Form.Control
                      type="text"
                      name="numero_cuenta"
                      value={cuentaTemporal.numero_cuenta}
                      onChange={handleCuentaTemporalInputChange}
                      placeholder="123456789"
                      size="sm"
                    />
                  </Form.Group>
                  
                  {cuentaTemporal.tipo_cuenta_bancaria_id && tiposCuentaBancaria.find(t => t.id === parseInt(cuentaTemporal.tipo_cuenta_bancaria_id))?.requiere_codigo && (
                    <Form.Group className="mb-2">
                      <Form.Label>Código *</Form.Label>
                      <Form.Control
                        type="text"
                        name="codigo_cuenta"
                        value={cuentaTemporal.codigo_cuenta}
                        onChange={handleCuentaTemporalInputChange}
                        placeholder="Código"
                        size="sm"
                      />
                    </Form.Group>
                  )}
                  
                  <Form.Check
                    type="checkbox"
                    name="es_principal"
                    checked={cuentaTemporal.es_principal}
                    onChange={handleCuentaTemporalInputChange}
                    label="Cuenta principal"
                    className="mb-2"
                  />
                  
                  <Button variant="success" size="sm" onClick={agregarCuentaBancaria} className="w-100">
                    <FaPlus className="me-2" />
                    Agregar
                  </Button>
                </Card.Body>
              </Card>
              
              {formDataBeneficiario.cuentas_bancarias.length > 0 && (
                <div className="mb-3">
                  {formDataBeneficiario.cuentas_bancarias.map((cuenta, index) => {
                    const tipoCuenta = tiposCuentaBancaria.find(t => t.id === parseInt(cuenta.tipo_cuenta_bancaria_id));
                    return (
                      <Card key={index} className="mb-2" style={{fontSize: '0.85rem'}}>
                        <Card.Body className="p-2">
                          <div className="d-flex justify-content-between align-items-start">
                            <div style={{flex: 1}}>
                              <div><strong>{tipoCuenta?.nombre || 'N/A'}</strong></div>
                              <div>{cuenta.banco} - {cuenta.numero_cuenta}</div>
                              {cuenta.codigo_cuenta && <div>Código: {cuenta.codigo_cuenta}</div>}
                              {cuenta.es_principal && <Badge bg="primary" className="mt-1">Principal</Badge>}
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => eliminarCuentaBancaria(index)}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              )}
              
              {formDataBeneficiario.cuentas_bancarias.length === 0 && (
                <Alert variant="warning" style={{fontSize: '0.85rem'}}>
                  Debe agregar al menos una cuenta
                </Alert>
              )}
              
              <hr className="my-3" />
              
              <Form.Check
                type="checkbox"
                label="¿Datos de contacto?"
                checked={mostrarDatosContacto}
                onChange={(e) => setMostrarDatosContacto(e.target.checked)}
                className="mb-3"
              />
              
              {mostrarDatosContacto && (
                <>
                  <Form.Group className="mb-2">
                    <Form.Label>Nombre Contacto</Form.Label>
                    <Form.Control
                      type="text"
                      name="contacto_nombre"
                      value={formDataBeneficiario.contacto_nombre}
                      onChange={handleBeneficiarioInputChange}
                      size="sm"
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-2">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="contacto_email"
                      value={formDataBeneficiario.contacto_email}
                      onChange={handleBeneficiarioInputChange}
                      size="sm"
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-2">
                    <Form.Label>Teléfono</Form.Label>
                    <Form.Control
                      type="text"
                      name="contacto_telefono"
                      value={formDataBeneficiario.contacto_telefono}
                      onChange={handleBeneficiarioInputChange}
                      size="sm"
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-2">
                    <Form.Label>Dirección</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="direccion"
                      value={formDataBeneficiario.direccion}
                      onChange={handleBeneficiarioInputChange}
                      size="sm"
                    />
                  </Form.Group>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => { setShowModalBeneficiario(false); setMostrarDatosContacto(false); }}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={loading || formDataBeneficiario.cuentas_bancarias.length === 0}>
                {loading ? <Spinner size="sm" /> : 'Crear'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Modal confirmar depósito - Versión móvil */}
        <Modal show={showModalConfirmarDeposito} onHide={resetConfirmarDepositoModal} fullscreen="sm-down">
          <Modal.Header closeButton>
            <Modal.Title style={{fontSize: '1.1rem'}}>Confirmar Depósito</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {depositoParaConfirmar && (
              <Alert variant="info" className="mb-3">
                <strong>Depósito #{depositoParaConfirmar.id}</strong><br />
                <div className="mt-2">
                  <div className="deposit-amount">
                    Neto a depositar: {depositoParaConfirmar.moneda}{' '}
                    {parseFloat(depositoParaConfirmar.monto).toFixed(2)}
                  </div>
                  {Number(depositoParaConfirmar.monto_retencion) > 0 &&
                    depositoParaConfirmar.monto_factura != null && (
                    <small className="d-block text-muted">
                      Factura: {parseFloat(depositoParaConfirmar.monto_factura).toFixed(2)} — Retención:{' '}
                      {parseFloat(depositoParaConfirmar.monto_retencion).toFixed(2)}
                    </small>
                  )}
                  <small>
                    Beneficiario:{' '}
                    {depositoParaConfirmar.beneficiario_nombre ||
                      depositoParaConfirmar.beneficiario ||
                      '— (sin asignar)'}
                  </small>
                </div>
              </Alert>
            )}
            
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Fecha de pago *</Form.Label>
                <Form.Control
                  type="date"
                  value={fechaPagoConfirmacion}
                  onChange={(e) => setFechaPagoConfirmacion(e.target.value)}
                  required
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Método de Pago *</Form.Label>
                <Form.Select
                  value={metodoPagoConfirmacion || ''}
                  onChange={(e) => setMetodoPagoConfirmacion(e.target.value)}
                  required
                  disabled={loadingMetodosPago}
                >
                  <option value="">
                    {loadingMetodosPago ? 'Cargando...' : 'Seleccione...'}
                  </option>
                  {metodosPago && metodosPago.length > 0 ? (
                    metodosPago.map(metodo => (
                      <option key={metodo.id} value={metodo.nombre}>
                        {metodo.nombre}
                      </option>
                    ))
                  ) : !loadingMetodosPago ? (
                    <option disabled>No disponible</option>
                  ) : null}
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Número de Operación</Form.Label>
                <Form.Control
                  type="text"
                  value={numeroOperacionConfirmacion}
                  onChange={(e) => setNumeroOperacionConfirmacion(e.target.value)}
                  placeholder="Opcional"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Comprobante del depósito (imagen, opcional)</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                  onChange={handleComprobanteConfirmacionChange}
                  disabled={loading}
                />
                <Form.Text className="text-muted">JPG, PNG, WEBP o GIF. Máximo recomendado 8 MB.</Form.Text>
                {comprobanteConfirmacionPreview && (
                  <div className="mt-2 text-center">
                    <img
                      src={comprobanteConfirmacionPreview}
                      alt="Vista previa comprobante"
                      style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: 8, border: '1px solid #dee2e6' }}
                    />
                    <div>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="p-0 mt-1"
                        onClick={() => {
                          limpiarComprobanteConfirmacion();
                        }}
                      >
                        Quitar imagen
                      </Button>
                    </div>
                  </div>
                )}
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={resetConfirmarDepositoModal}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleConfirmarDepositoSubmit} disabled={loading}>
              {loading ? <Spinner size="sm" /> : 'Confirmar'}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showModalVerComprobante} onHide={cerrarModalVerComprobante} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>Comprobante de depósito</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            {verComprobanteObjectUrl ? (
              <img src={verComprobanteObjectUrl} alt="Comprobante" style={{ maxWidth: '100%', maxHeight: '70vh' }} />
            ) : (
              <Spinner animation="border" />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={cerrarModalVerComprobante}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showModalRetencionParams} onHide={() => setShowModalRetencionParams(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Parámetros retención proveedores (PEN, USD, EUR)</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSaveRetencionParams}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Porcentaje de retención (%)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formRetencionParams.porcentaje}
                  onChange={(e) =>
                    setFormRetencionParams((p) => ({ ...p, porcentaje: e.target.value }))
                  }
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Monto mínimo de factura (en la moneda del depósito)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={formRetencionParams.monto_minimo_factura}
                  onChange={(e) =>
                    setFormRetencionParams((p) => ({ ...p, monto_minimo_factura: e.target.value }))
                  }
                  required
                />
                <Form.Text className="text-muted">
                  La retención solo aplica si el total de la factura es estrictamente mayor a este valor, expresado en
                  la misma moneda del depósito (PEN, USD o EUR).
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" type="button" onClick={() => setShowModalRetencionParams(false)}>
                Cerrar
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? <Spinner size="sm" /> : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
        {renderModalDesconfirmarDeposito()}
        {renderModalDesaprobarDeposito()}
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <ToastContainer />
      <style>
        {`
          .dh-card-main {
            border-radius: 16px;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
            border: none;
          }
          .dh-card-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            padding: 1.5rem;
          }
          .dh-header-title {
            font-weight: 600;
            color: white;
          }
        `}
      </style>

      <Row>
        <Col>
          <Card className="dh-card-main mb-4">
            <Card.Header className="dh-card-header">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <FaMoneyBillWave className="me-3" size={28} />
                  <h3 className="dh-header-title mb-0">Gestión de Solicitudes de Depósito</h3>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    onClick={handleExportPDF}
                    disabled={loading}
                  >
                    <FaFilePdf className="me-2" />
                    Exportar a PDF
                  </Button>
                  <Button
                    variant="info"
                    onClick={handleExportExcelResumen}
                    disabled={loading}
                  >
                    <FaFilePdf className="me-2" />
                    Resumen por Banco
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleExportExcelDetallado}
                    disabled={loading}
                  >
                    <FaFileExcel className="me-2" />
                    Reporte Detallado
                  </Button>
                  <Button
                    variant="light"
                    onClick={() => {
                      resetForm();
                      setShowModal(true);
                    }}
                  >
                    <FaPlus className="me-2" />
                    Nueva Solicitud de Depósito
                  </Button>
                  <Button variant="outline-light" onClick={openModalRetencionParams}>
                    <FaCog className="me-2" />
                    Retención
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {/* Filtros */}
              <Card className="mb-4">
                <Card.Header>
                  <FaFilter className="me-2" />
                  Filtros
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Centro de costo</Form.Label>
                        <Form.Select
                          name="centro_costo_id"
                          value={filters.centro_costo_id}
                          onChange={handleFilterChange}
                        >
                          <option value="">Todos</option>
                          {centrosCosto.map((cc) => (
                            <option key={cc.id} value={cc.id}>
                              {cc.codigo} — {cc.nombre}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Tipo de Depósito</Form.Label>
                        <Form.Select
                          name="tipo_deposito_id"
                          value={filters.tipo_deposito_id}
                          onChange={handleFilterChange}
                        >
                          <option value="">Todos</option>
                          {tiposDeposito.map(tipo => (
                            <option key={tipo.id} value={tipo.id}>
                              {tipo.nombre}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>Estado</Form.Label>
                        <Form.Select
                          name="estado"
                          value={filters.estado}
                          onChange={handleFilterChange}
                        >
                          <option value="">Todos</option>
                          <option value="registrado">Registrado</option>
                          <option value="aprobado">Aprobado</option>
                          <option value="cancelado">Cancelado</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>Fecha Desde</Form.Label>
                        <Form.Control
                          type="date"
                          name="fecha_desde"
                          value={filters.fecha_desde}
                          onChange={handleFilterChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>Fecha Hasta</Form.Label>
                        <Form.Control
                          type="date"
                          name="fecha_hasta"
                          value={filters.fecha_hasta}
                          onChange={handleFilterChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Tabla de depósitos */}
              {loading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Cargando depósitos...</p>
                </div>
              ) : depositos.length === 0 ? (
                <Alert variant="info">No se encontraron depósitos</Alert>
              ) : (
                <>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Centro de costo</th>
                        <th>Tipo</th>
                        <th>Beneficiario</th>
                        <th>Descripción</th>
                        <th>Fecha Solicitud</th>
                        <th>Fecha Pago</th>
                        <th>F. registro (auditoría)</th>
                        <th>Monto</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {depositos.map(deposito => (
                        <tr key={deposito.id}>
                          <td>{deposito.id}</td>
                          <td>{deposito.centro_costo_nombre || '-'}</td>
                          <td>{deposito.tipo_deposito_nombre || deposito.tipo_deposito_codigo || '-'}</td>
                          <td>{deposito.beneficiario_nombre || deposito.beneficiario || '-'}</td>
                          <td>
                            {deposito.descripcion ? (
                              <div style={{maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}} title={deposito.descripcion}>
                                {deposito.descripcion}
                              </div>
                            ) : '-'}
                          </td>
                          <td>{deposito.fecha_solicitud || '-'}</td>
                          <td>{deposito.fecha_pago || '-'}</td>
                          <td className="text-nowrap small">{formatAuditoriaCreacion(deposito.created_at)}</td>
                          <td>
                            <div>{deposito.moneda} {parseFloat(deposito.monto).toFixed(2)}</div>
                            {Number(deposito.monto_retencion) > 0 && deposito.monto_factura != null && (
                              <small className="text-muted d-block" style={{ maxWidth: '140px' }}>
                                Fact. {parseFloat(deposito.monto_factura).toFixed(2)} — Ret.{' '}
                                {parseFloat(deposito.monto_retencion).toFixed(2)}
                              </small>
                            )}
                          </td>
                          <td>{getEstadoBadge(deposito)}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleEdit(deposito)}
                                disabled={deposito.estado === 'aprobado' || deposito.estado === 'cancelado'}
                              >
                                <FaEdit />
                              </Button>
                              {deposito.estado === 'registrado' && canApprove && (
                                <Button
                                  size="sm"
                                  variant="outline-success"
                                  onClick={() => handleAprobar(deposito.id)}
                                  title="Aprobar depósito"
                                >
                                  <FaCheck />
                                </Button>
                              )}
                              {deposito.estado === 'aprobado' && !tieneFechaPagoConfirmada(deposito) && (
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log('🖱️ [Depositos] Botón Confirmar Depósito clickeado. Depósito:', deposito);
                                    handleConfirmarDeposito(deposito);
                                  }}
                                  title="Confirmar depósito"
                                >
                                  <FaCheckCircle />
                                </Button>
                              )}
                              {puedeDesaprobarDeposito(deposito) && (
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => abrirModalDesaprobar(deposito)}
                                  disabled={loading}
                                  title="Desaprobar: vuelve a Solicitado y permite editar"
                                >
                                  <FaReply />
                                </Button>
                              )}
                              {esDepositoRealizado(deposito) && (
                                <Button
                                  size="sm"
                                  variant="outline-warning"
                                  onClick={() => abrirModalDesconfirmar(deposito)}
                                  disabled={loading}
                                  title="Desconfirmar Realizado: vuelve a Aprobado (fecha de pago y comprobante)"
                                >
                                  <FaUndo />
                                </Button>
                              )}
                              {deposito.comprobante_deposito_path && (
                                <Button
                                  size="sm"
                                  variant="outline-info"
                                  onClick={() => abrirVerComprobanteDeposito(deposito.id)}
                                  disabled={loadingVerComprobante}
                                  title="Ver imagen del comprobante del depósito"
                                >
                                  <FaImage />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleDelete(deposito.id)}
                                disabled={deposito.estado === 'eliminado'}
                                title="Eliminar depósito permanentemente"
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  
                  {/* Paginación */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      Mostrando {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} de {total}
                    </div>
                    <div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        Anterior
                      </Button>
                      <span className="mx-2">Página {page}</span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setPage(p => p + 1)}
                        disabled={page * pageSize >= total}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal para crear/editar depósito */}
      <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingDeposito ? 'Editar Solicitud de Depósito' : 'Nueva Solicitud de Depósito'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Centro de costo *</Form.Label>
                  <Form.Select
                    name="centro_costo_id"
                    value={formData.centro_costo_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seleccione un centro de costo</option>
                    {centrosCosto.map((cc) => (
                      <option key={cc.id} value={cc.id}>
                        {cc.codigo} — {cc.nombre}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Depósito *</Form.Label>
                  <Form.Select
                    name="tipo_deposito_id"
                    value={formData.tipo_deposito_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seleccione un tipo</option>
                    {tiposDeposito.map(tipo => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.nombre}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha de solicitud *</Form.Label>
                  <Form.Control
                    type="date"
                    name="fecha_solicitud"
                    value={formData.fecha_solicitud}
                    onChange={handleInputChange}
                    required
                  />
                  <Form.Text className="text-muted">Por defecto la fecha de hoy; puede modificarla.</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {editingDeposito && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Número de Documento</Form.Label>
                    <Form.Control
                      type="text"
                      name="numero_documento"
                      value={formData.numero_documento}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {aplicaVistaRetencionProveedor()
                      ? `Monto total factura (${formData.moneda || 'PEN'}) *`
                      : 'Monto *'}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="monto"
                    value={formData.monto}
                    onChange={handleInputChange}
                    required
                    min="0.01"
                  />
                  {(() => {
                    const pr = getRetencionPreview();
                    if (!pr.muestra) return null;
                    if (pr.sinRetPorMinimo) {
                      return (
                        <Alert variant="info" className="mt-2 py-2 mb-0" style={{ fontSize: '0.85rem' }}>
                          No aplica retención: el monto no supera {simboloMonedaRetencion(formData.moneda)}{' '}
                          {Number(retencionParams.monto_minimo_factura).toFixed(2)} (mismo umbral en{' '}
                          {formData.moneda || 'PEN'}, según parámetros).
                        </Alert>
                      );
                    }
                    if (pr.retencion > 0) {
                      return (
                        <Alert variant="warning" className="mt-2 py-2 mb-0" style={{ fontSize: '0.85rem' }}>
                          Retención ({retencionParams.porcentaje}%): {simboloMonedaRetencion(formData.moneda)}{' '}
                          {pr.retencion.toFixed(2)} — Monto a depositar (neto):{' '}
                          {simboloMonedaRetencion(formData.moneda)}
                          {pr.neto.toFixed(2)}
                        </Alert>
                      );
                    }
                    return null;
                  })()}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Moneda *</Form.Label>
                  <Form.Select
                    name="moneda"
                    value={formData.moneda}
                    onChange={handleInputChange}
                  >
                    <option value="PEN">PEN (Soles)</option>
                    <option value="USD">USD (Dólares)</option>
                    <option value="EUR">EUR (Euros)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <hr className="my-3" />

            {/* Selector de modo de depósito */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">Tipo de Registro *</Form.Label>
              <div className="d-flex flex-wrap gap-4">
                <Form.Check
                  type="radio"
                  id="modo-con-beneficiario"
                  name="modo_deposito"
                  label="Con Beneficiario/Proveedor/Servicio Registrado"
                  checked={modoDeposito === 'con_beneficiario'}
                  onChange={() => setModoDeposito('con_beneficiario')}
                />
                <Form.Check
                  type="radio"
                  id="modo-libre"
                  name="modo_deposito"
                  label="Ingreso Libre (sin registrar)"
                  checked={modoDeposito === 'libre'}
                  onChange={() => setModoDeposito('libre')}
                />
                <Form.Check
                  type="radio"
                  id="modo-sin-dest"
                  name="modo_deposito"
                  label="Registro sin beneficiario ni cuenta (temporal)"
                  checked={modoDeposito === 'sin_destinatario'}
                  onChange={() => setModoDeposito('sin_destinatario')}
                />
              </div>
              {modoDeposito === 'sin_destinatario' && (
                <Alert variant="warning" className="mt-2 py-2 mb-0" style={{ fontSize: '0.9rem' }}>
                  Permite registrar el depósito con monto, centro de costo y tipo, sin beneficiario ni cuenta bancaria.
                  Es una vía temporal: el destinatario y la cuenta se podrán asociar más adelante cuando exista el proceso
                  o pantalla correspondiente.
                </Alert>
              )}
            </Form.Group>

            {/* Modo: Con Beneficiario */}
            {modoDeposito === 'con_beneficiario' && (
              <>
                <Row>
                  <Col md={10}>
                    <Form.Group className="mb-3">
                      <Form.Label>Beneficiario / proveedor / servicio *</Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type="text"
                          autoComplete="off"
                          placeholder="Buscar por nombre…"
                          value={beneficiarioBusqueda}
                          onChange={handleBeneficiarioBusquedaChange}
                          onFocus={() => setBeneficiarioSugerenciasAbiertas(true)}
                          onBlur={() => {
                            beneficiarioBlurTimerRef.current = setTimeout(() => {
                              setBeneficiarioSugerenciasAbiertas(false);
                            }, 200);
                          }}
                        />
                        {beneficiarioSugerenciasAbiertas && beneficiarioBusqueda.trim() && (
                          <>
                            {beneficiariosFiltrados.length > 0 ? (
                              <ListGroup
                                className="position-absolute w-100 shadow-sm mt-1 rounded"
                                style={{ zIndex: 1060, maxHeight: 240, overflowY: 'auto' }}
                              >
                                {beneficiariosFiltrados.map((b) => (
                                  <ListGroup.Item
                                    key={b.id}
                                    action
                                    as="button"
                                    type="button"
                                    className="py-2 text-start"
                                    onMouseDown={(ev) => ev.preventDefault()}
                                    onClick={() => seleccionarBeneficiario(b)}
                                  >
                                    {etiquetaBeneficiarioLista(b)}
                                  </ListGroup.Item>
                                ))}
                              </ListGroup>
                            ) : (
                              <div
                                className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 px-2 py-2 small text-muted"
                                style={{ zIndex: 1060 }}
                              >
                                Sin coincidencias
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <Form.Text muted>Escriba para filtrar y elija una fila de la lista.</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Label>&nbsp;</Form.Label>
                    <div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setShowModalBeneficiario(true)}
                        className="w-100"
                      >
                        <FaPlus /> Nuevo
                      </Button>
                    </div>
                  </Col>
                </Row>

                {formData.beneficiario_id && (
                  <>
                    <Row>
                      <Col md={10}>
                        <Form.Group className="mb-3">
                          <Form.Label>Cuenta Bancaria *</Form.Label>
                          <Form.Select
                            name="cuenta_bancaria_id"
                            value={formData.cuenta_bancaria_id}
                            onChange={handleInputChange}
                            required={modoDeposito === 'con_beneficiario' && formData.beneficiario_id}
                          >
                            <option value="">Seleccione una cuenta</option>
                            {cuentasBancarias.map(cuenta => (
                              <option key={cuenta.id} value={cuenta.id}>
                                {cuenta.tipo_cuenta_nombre || cuenta.tipo_cuenta || 'N/A'} - {cuenta.banco} - {cuenta.numero_cuenta}
                                {cuenta.codigo_cuenta ? ` - ${cuenta.codigo_cuenta}` : ''}
                                {cuenta.es_principal ? ' ★' : ''}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Label>&nbsp;</Form.Label>
                        <div>
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => {
                              setFormDataCuenta(prev => ({ ...prev, beneficiario_id: formData.beneficiario_id }));
                              setShowModalCuenta(true);
                            }}
                            className="w-100"
                            disabled={!formData.beneficiario_id}
                          >
                            <FaPlus /> Cuenta
                          </Button>
                        </div>
                      </Col>
                    </Row>

                    {/* Mostrar información de la cuenta seleccionada */}
                    {formData.cuenta_bancaria_id && (
                      <Card className="mb-3" style={{backgroundColor: '#f8f9fa'}}>
                        <Card.Body>
                          <h6 className="mb-2">Información de la Cuenta Seleccionada:</h6>
                          {(() => {
                            const cuentaSeleccionada = cuentasBancarias.find(c => c.id === parseInt(formData.cuenta_bancaria_id));
                            if (!cuentaSeleccionada) return null;
                            return (
                              <Row>
                                <Col md={3}>
                                  <small className="text-muted">Tipo de Cuenta:</small>
                                  <div className="fw-bold">{cuentaSeleccionada.tipo_cuenta_nombre || cuentaSeleccionada.tipo_cuenta || 'N/A'}</div>
                                </Col>
                                <Col md={3}>
                                  <small className="text-muted">Banco:</small>
                                  <div className="fw-bold">{cuentaSeleccionada.banco}</div>
                                </Col>
                                <Col md={3}>
                                  <small className="text-muted">Número de Cuenta:</small>
                                  <div className="fw-bold">{cuentaSeleccionada.numero_cuenta}</div>
                                </Col>
                                {cuentaSeleccionada.codigo_cuenta && (
                                  <Col md={3}>
                                    <small className="text-muted">Código:</small>
                                    <div className="fw-bold">{cuentaSeleccionada.codigo_cuenta}</div>
                                  </Col>
                                )}
                              </Row>
                            );
                          })()}
                        </Card.Body>
                      </Card>
                    )}
                  </>
                )}
              </>
            )}

            {/* Modo: Libre */}
            {modoDeposito === 'libre' && (
              <>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nombre del Beneficiario *</Form.Label>
                      <Form.Control
                        type="text"
                        name="beneficiario"
                        value={formData.beneficiario}
                        onChange={handleInputChange}
                        placeholder="Ingrese el nombre del beneficiario"
                        required={modoDeposito === 'libre'}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tipo de Cuenta *</Form.Label>
                      <Form.Select
                        name="tipo_cuenta_bancaria_id"
                        value={formData.tipo_cuenta_bancaria_id}
                        onChange={handleInputChange}
                        required={modoDeposito === 'libre'}
                      >
                        <option value="">Seleccione...</option>
                        {tiposCuentaBancaria.map(tipo => (
                          <option key={tipo.id} value={tipo.id}>
                            {tipo.nombre}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Banco *</Form.Label>
                      <Form.Select
                        name="banco"
                        value={formData.banco}
                        onChange={handleInputChange}
                        required={modoDeposito === 'libre'}
                      >
                        <option value="">Seleccione un banco</option>
                        {bancos.map(banco => (
                          <option key={banco.id} value={banco.nombre}>
                            {banco.nombre} {banco.nombre_corto ? `(${banco.nombre_corto})` : ''}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Número de Cuenta *</Form.Label>
                      <Form.Control
                        type="text"
                        name="cuenta_bancaria"
                        value={formData.cuenta_bancaria}
                        onChange={handleInputChange}
                        placeholder="Ej: 123456789"
                        required={modoDeposito === 'libre'}
                      />
                    </Form.Group>
                  </Col>
                  {(() => {
                    const tipoSeleccionado = tiposCuentaBancaria.find(t => t.id === parseInt(formData.tipo_cuenta_bancaria_id));
                    return tipoSeleccionado?.requiere_codigo && (
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Código *</Form.Label>
                          <Form.Control
                            type="text"
                            name="codigo_cuenta"
                            value={formData.codigo_cuenta}
                            onChange={handleInputChange}
                            placeholder="Código de cuenta recaudadora"
                            required
                          />
                        </Form.Group>
                      </Col>
                    );
                  })()}
                </Row>

                {/* Mostrar información ingresada */}
                {formData.beneficiario && formData.banco && formData.cuenta_bancaria && (
                  <Card className="mb-3" style={{backgroundColor: '#f8f9fa'}}>
                    <Card.Body>
                      <h6 className="mb-2">Información Ingresada:</h6>
                      <Row>
                        <Col md={3}>
                          <small className="text-muted">Beneficiario:</small>
                          <div className="fw-bold">{formData.beneficiario}</div>
                        </Col>
                        {formData.tipo_cuenta_bancaria_id && (
                          <Col md={3}>
                            <small className="text-muted">Tipo de Cuenta:</small>
                            <div className="fw-bold">{tiposCuentaBancaria.find(t => t.id === parseInt(formData.tipo_cuenta_bancaria_id))?.nombre || 'N/A'}</div>
                          </Col>
                        )}
                        <Col md={2}>
                          <small className="text-muted">Banco:</small>
                          <div className="fw-bold">{formData.banco}</div>
                        </Col>
                        <Col md={2}>
                          <small className="text-muted">Número:</small>
                          <div className="fw-bold">{formData.cuenta_bancaria}</div>
                        </Col>
                        {formData.codigo_cuenta && (
                          <Col md={2}>
                            <small className="text-muted">Código:</small>
                            <div className="fw-bold">{formData.codigo_cuenta}</div>
                          </Col>
                        )}
                      </Row>
                    </Card.Body>
                  </Card>
                )}
              </>
            )}

            <hr className="my-3" />

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Descripción *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleInputChange}
                    placeholder="Describa el motivo del depósito"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Observaciones</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="observaciones"
                    value={formData.observaciones}
                    onChange={handleInputChange}
                    placeholder="Observaciones adicionales (opcional)"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : (editingDeposito ? 'Actualizar' : 'Crear')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para crear beneficiario */}
      <Modal 
        show={showModalBeneficiario} 
        onHide={() => { setShowModalBeneficiario(false); setMostrarDatosContacto(false); }} 
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Nuevo Beneficiario/Proveedor/Servicio</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateBeneficiario}>
          <Modal.Body>
            <h6 className="mb-3">Datos Generales</h6>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo *</Form.Label>
                  <Form.Select
                    name="tipo"
                    value={formDataBeneficiario.tipo}
                    onChange={handleBeneficiarioInputChange}
                    required
                  >
                    <option value="proveedor">Proveedor</option>
                    <option value="beneficiario">Beneficiario</option>
                    <option value="servicio">Servicio</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    El código se generará automáticamente (PROV-000001, BEN-000001, SERV-000001)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            {formDataBeneficiario.tipo === 'proveedor' && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="excluido_retencion"
                  label="Proveedor excluido de retención (agente de retención / no sujeto)"
                  checked={!!formDataBeneficiario.excluido_retencion}
                  onChange={handleBeneficiarioInputChange}
                />
              </Form.Group>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Nombre / Razón Social *</Form.Label>
              <Form.Control
                type="text"
                name="nombre"
                value={formDataBeneficiario.nombre}
                onChange={handleBeneficiarioInputChange}
                required
                placeholder={
                  formDataBeneficiario.tipo === 'proveedor' 
                    ? 'Ej: Empresa Construcciones SAC' 
                    : formDataBeneficiario.tipo === 'beneficiario'
                    ? 'Ej: Juan Pérez García'
                    : 'Ej: Servicio de Transporte Express'
                }
              />
              <Form.Text className="text-muted">
                {formDataBeneficiario.tipo === 'proveedor' && 'Ingrese la razón social o nombre comercial del proveedor'}
                {formDataBeneficiario.tipo === 'beneficiario' && 'Ingrese el nombre completo del beneficiario'}
                {formDataBeneficiario.tipo === 'servicio' && 'Ingrese el nombre del servicio'}
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Número de Documento (RUC, DNI, etc.)</Form.Label>
              <Form.Control
                type="text"
                name="numero_documento"
                value={formDataBeneficiario.numero_documento}
                onChange={handleBeneficiarioInputChange}
                placeholder="Ej: 20123456789"
              />
            </Form.Group>
            
            <hr className="my-4" />
            
            <h6 className="mb-3">Cuentas Bancarias *</h6>
            <Card className="mb-3" style={{backgroundColor: '#f8f9fa'}}>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tipo de Cuenta *</Form.Label>
                      <Form.Select
                        name="tipo_cuenta_bancaria_id"
                        value={cuentaTemporal.tipo_cuenta_bancaria_id}
                        onChange={handleCuentaTemporalInputChange}
                      >
                        <option value="">Seleccione...</option>
                        {tiposCuentaBancaria.map(tipo => (
                          <option key={tipo.id} value={tipo.id}>
                            {tipo.nombre}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Banco *</Form.Label>
                      <Form.Select
                        name="banco"
                        value={cuentaTemporal.banco}
                        onChange={handleCuentaTemporalInputChange}
                      >
                        <option value="">Seleccione...</option>
                        {bancos.map(banco => (
                          <option key={banco.id} value={banco.nombre}>
                            {banco.nombre}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Número de Cuenta *</Form.Label>
                      <Form.Control
                        type="text"
                        name="numero_cuenta"
                        value={cuentaTemporal.numero_cuenta}
                        onChange={handleCuentaTemporalInputChange}
                        placeholder="Ej: 123456789"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Principal</Form.Label>
                      <div className="mt-2">
                        <Form.Check
                          type="checkbox"
                          name="es_principal"
                          checked={cuentaTemporal.es_principal}
                          onChange={handleCuentaTemporalInputChange}
                          label="Sí"
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                {cuentaTemporal.tipo_cuenta_bancaria_id && tiposCuentaBancaria.find(t => t.id === parseInt(cuentaTemporal.tipo_cuenta_bancaria_id))?.requiere_codigo && (
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Código de Cuenta Recaudadora *</Form.Label>
                        <Form.Control
                          type="text"
                          name="codigo_cuenta"
                          value={cuentaTemporal.codigo_cuenta}
                          onChange={handleCuentaTemporalInputChange}
                          placeholder="Ingrese el código de la cuenta recaudadora"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                <Button variant="success" size="sm" onClick={agregarCuentaBancaria}>
                  <FaPlus className="me-2" />
                  Agregar Cuenta
                </Button>
              </Card.Body>
            </Card>
            
            {formDataBeneficiario.cuentas_bancarias.length > 0 && (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th style={{width: '25%'}}>Tipo de Cuenta</th>
                      <th style={{width: '20%'}}>Banco</th>
                      <th style={{width: '20%'}}>Número de Cuenta</th>
                      <th style={{width: '15%'}}>Código</th>
                      <th style={{width: '10%'}}>Principal</th>
                      <th style={{width: '10%'}}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formDataBeneficiario.cuentas_bancarias.map((cuenta, index) => {
                      const tipoCuenta = tiposCuentaBancaria.find(t => t.id === parseInt(cuenta.tipo_cuenta_bancaria_id));
                      return (
                        <tr key={index}>
                          <td>{tipoCuenta?.nombre || 'N/A'}</td>
                          <td>{cuenta.banco}</td>
                          <td>{cuenta.numero_cuenta}</td>
                          <td>{cuenta.codigo_cuenta || '-'}</td>
                          <td className="text-center">{cuenta.es_principal ? <Badge bg="primary">Sí</Badge> : 'No'}</td>
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => eliminarCuentaBancaria(index)}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
            
            {formDataBeneficiario.cuentas_bancarias.length === 0 && (
              <Alert variant="warning">
                Debe agregar al menos una cuenta bancaria
              </Alert>
            )}
            
            <hr className="my-4" />
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="¿Desea ingresar datos de contacto?"
                checked={mostrarDatosContacto}
                onChange={(e) => setMostrarDatosContacto(e.target.checked)}
              />
            </Form.Group>
            
            {mostrarDatosContacto && (
              <>
                <h6 className="mb-3">Datos de Contacto (Opcional)</h6>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nombre del Contacto</Form.Label>
                      <Form.Control
                        type="text"
                        name="contacto_nombre"
                        value={formDataBeneficiario.contacto_nombre}
                        onChange={handleBeneficiarioInputChange}
                        placeholder="Ej: Juan Pérez"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email de Contacto</Form.Label>
                      <Form.Control
                        type="email"
                        name="contacto_email"
                        value={formDataBeneficiario.contacto_email}
                        onChange={handleBeneficiarioInputChange}
                        placeholder="ejemplo@correo.com"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Teléfono de Contacto</Form.Label>
                      <Form.Control
                        type="text"
                        name="contacto_telefono"
                        value={formDataBeneficiario.contacto_telefono}
                        onChange={handleBeneficiarioInputChange}
                        placeholder="999 999 999"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Dirección</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="direccion"
                    value={formDataBeneficiario.direccion}
                    onChange={handleBeneficiarioInputChange}
                    placeholder="Ingrese la dirección completa"
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowModalBeneficiario(false); setMostrarDatosContacto(false); }}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading || formDataBeneficiario.cuentas_bancarias.length === 0}>
              {loading ? <Spinner size="sm" /> : 'Crear'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para crear cuenta bancaria */}
      <Modal show={showModalCuenta} onHide={() => setShowModalCuenta(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Nueva Cuenta Bancaria</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateCuenta}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Beneficiario *</Form.Label>
              <Form.Select
                name="beneficiario_id"
                value={formDataCuenta.beneficiario_id}
                onChange={handleCuentaInputChange}
                required
                disabled={!!formData.beneficiario_id}
              >
                <option value="">Seleccione un beneficiario</option>
                {beneficiarios.map(benef => (
                  <option key={benef.id} value={benef.id}>
                    {benef.nombre}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Banco *</Form.Label>
                  <Form.Control
                    type="text"
                    name="banco"
                    value={formDataCuenta.banco}
                    onChange={handleCuentaInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Moneda</Form.Label>
                  <Form.Select
                    name="moneda"
                    value={formDataCuenta.moneda}
                    onChange={handleCuentaInputChange}
                  >
                    <option value="PEN">PEN</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Número de Cuenta *</Form.Label>
                  <Form.Control
                    type="text"
                    name="numero_cuenta"
                    value={formDataCuenta.numero_cuenta}
                    onChange={handleCuentaInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Cuenta *</Form.Label>
                  <Form.Select
                    name="tipo_cuenta"
                    value={formDataCuenta.tipo_cuenta}
                    onChange={handleCuentaInputChange}
                    required
                  >
                    <option value="">Seleccione...</option>
                    <option value="Ahorros">Ahorros</option>
                    <option value="Cuenta Corriente">Cuenta Corriente</option>
                    <option value="Cuenta Recaudadora">Cuenta Recaudadora</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            {formDataCuenta.tipo_cuenta === 'Cuenta Recaudadora' && (
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Código de Cuenta Recaudadora *</Form.Label>
                    <Form.Control
                      type="text"
                      name="codigo_cuenta"
                      value={formDataCuenta.codigo_cuenta}
                      onChange={handleCuentaInputChange}
                      required
                      placeholder="Ingrese el código de la cuenta recaudadora"
                    />
                    <Form.Text className="text-muted">
                      Este código es obligatorio para cuentas recaudadoras.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="es_principal"
                checked={formDataCuenta.es_principal}
                onChange={handleCuentaInputChange}
                label="Marcar como cuenta principal"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModalCuenta(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : 'Crear'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para confirmar depósito */}
      <Modal show={showModalConfirmarDeposito} onHide={resetConfirmarDepositoModal}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Depósito</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Fecha de pago *</Form.Label>
              <Form.Control
                type="date"
                value={fechaPagoConfirmacion}
                onChange={(e) => setFechaPagoConfirmacion(e.target.value)}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Método de Pago *</Form.Label>
              <Form.Select
                value={metodoPagoConfirmacion || ''}
                onChange={(e) => setMetodoPagoConfirmacion(e.target.value)}
                required
                disabled={loadingMetodosPago}
              >
                <option value="">
                  {loadingMetodosPago ? 'Cargando...' : 'Seleccione un método de pago'}
                </option>
                {metodosPago && metodosPago.length > 0 ? (
                  metodosPago.map(metodo => (
                    <option key={metodo.id} value={metodo.nombre}>
                      {metodo.nombre}
                    </option>
                  ))
                ) : !loadingMetodosPago ? (
                  <option disabled>No hay métodos de pago disponibles</option>
                ) : null}
              </Form.Select>
              {metodosPago.length === 0 && !loadingMetodosPago && (
                <Form.Text className="text-danger">
                  ⚠️ No hay métodos de pago configurados. Contacte al administrador.
                </Form.Text>
              )}
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Número de Operación</Form.Label>
              <Form.Control
                type="text"
                value={numeroOperacionConfirmacion}
                onChange={(e) => setNumeroOperacionConfirmacion(e.target.value)}
                placeholder="Ingrese el número de operación bancaria (opcional)"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Comprobante del depósito (imagen, opcional)</Form.Label>
              <Form.Control
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                onChange={handleComprobanteConfirmacionChange}
                disabled={loading}
              />
              <Form.Text className="text-muted">JPG, PNG, WEBP o GIF. Máximo recomendado 8 MB.</Form.Text>
              {comprobanteConfirmacionPreview && (
                <div className="mt-2 text-center">
                  <img
                    src={comprobanteConfirmacionPreview}
                    alt="Vista previa comprobante"
                    style={{ maxWidth: '100%', maxHeight: '220px', borderRadius: 8, border: '1px solid #dee2e6' }}
                  />
                  <div>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="p-0 mt-1"
                      onClick={() => limpiarComprobanteConfirmacion()}
                    >
                      Quitar imagen
                    </Button>
                  </div>
                </div>
              )}
            </Form.Group>
            
            {depositoParaConfirmar && (
              <Alert variant="info">
                <strong>Depósito #{depositoParaConfirmar.id}</strong><br />
                Neto a depositar: {depositoParaConfirmar.moneda}{' '}
                {parseFloat(depositoParaConfirmar.monto).toFixed(2)}
                <br />
                {Number(depositoParaConfirmar.monto_retencion) > 0 &&
                  depositoParaConfirmar.monto_factura != null && (
                  <>
                    Factura: {parseFloat(depositoParaConfirmar.monto_factura).toFixed(2)} — Retención:{' '}
                    {parseFloat(depositoParaConfirmar.monto_retencion).toFixed(2)}
                    <br />
                  </>
                )}
                Beneficiario:{' '}
                {depositoParaConfirmar.beneficiario_nombre ||
                  depositoParaConfirmar.beneficiario ||
                  '— (sin asignar)'}
              </Alert>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetConfirmarDepositoModal}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleConfirmarDepositoSubmit} disabled={loading}>
            {loading ? <Spinner size="sm" /> : 'Confirmar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showModalVerComprobante} onHide={cerrarModalVerComprobante} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Comprobante de depósito</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {verComprobanteObjectUrl ? (
            <img src={verComprobanteObjectUrl} alt="Comprobante" style={{ maxWidth: '100%', maxHeight: '70vh' }} />
          ) : (
            <Spinner animation="border" />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cerrarModalVerComprobante}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showModalRetencionParams} onHide={() => setShowModalRetencionParams(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Parámetros retención proveedores (PEN, USD, EUR)</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveRetencionParams}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Porcentaje de retención (%)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formRetencionParams.porcentaje}
                onChange={(e) =>
                  setFormRetencionParams((p) => ({ ...p, porcentaje: e.target.value }))
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Monto mínimo de factura (en la moneda del depósito)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={formRetencionParams.monto_minimo_factura}
                onChange={(e) =>
                  setFormRetencionParams((p) => ({ ...p, monto_minimo_factura: e.target.value }))
                }
                required
              />
              <Form.Text className="text-muted">
                La retención solo aplica si el total de la factura es estrictamente mayor a este valor, expresado en
                la misma moneda del depósito (PEN, USD o EUR).
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={() => setShowModalRetencionParams(false)}>
              Cerrar
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : 'Guardar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      {renderModalDesconfirmarDeposito()}
      {renderModalDesaprobarDeposito()}
    </Container>
  );
};

export default Depositos;
